// src/components/Entity/EntityEditPage.js
// Edit page - Identical UX to WizardAddEntity Step 3
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  COMMON_CHARGE_LINES,
  getManagerTypeForLegalForm,
  calculateChargesFromBrut,
  calculateChargesFromNet,
  getLegalFormInfo,
} from '../../utils/entityConstants';
import apiService from '../../api';
import './EntityEditPage.css';
import './WizardAddEntity.css'; // For exercice-card styles

// Tooltip component with portal for overflow handling
const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 10,
        left: rect.left + rect.width / 2
      });
    }
    setShow(true);
  };

  return (
    <span
      ref={triggerRef}
      className="tooltip-trigger"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && ReactDOM.createPortal(
        <div
          className="tooltip-portal"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 999999
          }}
        >
          <div className="tooltip-content-portal">{text}</div>
        </div>,
        document.body
      )}
    </span>
  );
};

const EntityEditPage = ({ entityId, onClose, onSave, clearedManagerRoles = [], standalone = false, mode = 'scenario' }) => {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Identity (read-only)
  const [entityData, setEntityData] = useState({
    name: '', legalForm: '', taxRegime: '', activityType: '',
    creationDate: '', fiscalYearEnd: '31/12', shareCapital: '',
    firstExerciceType: 'long', // 'short' | 'long'
  });

  // Managers
  const [managers, setManagers] = useState([]);

  // Scenario individuals (for linking managers to persons)
  const [scenarioId, setScenarioId] = useState(null);
  const [scenarioIndividuals, setScenarioIndividuals] = useState([]);

  // Options
  const [hasACRE, setHasACRE] = useState(false);
  const [hasCGA, setHasCGA] = useState(false);
  const [tvaRegime, setTvaRegime] = useState('non_assujetti');
  const [dividendPercent, setDividendPercent] = useState(60);
  const [dividendPaymentDelay, setDividendPaymentDelay] = useState(6); // Mois après clôture
  const [advancedOptions, setAdvancedOptions] = useState({ jei: false, cir: false, cirAmount: 0 });

  // Employees
  const [hasEmployees, setHasEmployees] = useState(false);
  const [employeesData, setEmployeesData] = useState({ count: 1, avgGrossSalary: 2500, months: 12 });
  const [employeeProfiles, setEmployeeProfiles] = useState([]);

  // Charges
  const [chargeMode, setChargeMode] = useState('simplified');
  const [simplifiedCharges, setSimplifiedCharges] = useState({ totalPercent: 20, totalFixed: 25000, usePercent: false });
  const [detailedCharges, setDetailedCharges] = useState({});

  // Forecast & Exercices
  const [forecastDates, setForecastDatesState] = useState({ startDate: '', endDate: '' });
  const forecastDatesRef = useRef(forecastDates);
  // Wrapper that updates both state and ref synchronously
  const setForecastDates = (newDates) => {
    const dates = typeof newDates === 'function' ? newDates(forecastDatesRef.current) : newDates;
    forecastDatesRef.current = dates;
    setForecastDatesState(dates);
  };
  const [exerciceData, setExerciceDataState] = useState({});
  const exerciceDataRef = useRef(exerciceData);
  // Wrapper that updates both state and ref synchronously (avoids closure staleness)
  const setExerciceData = (newData) => {
    const data = typeof newData === 'function' ? newData(exerciceDataRef.current) : newData;
    exerciceDataRef.current = data;
    setExerciceDataState(data);
  };

  // Step 3 mode toggle (inherit from chargeMode or override)
  const [step3Mode, setStep3Mode] = useState(null);

  // Modal for charge details
  const [activeModal, setActiveModal] = useState(null);

  // Fiscal params for CFE/CVAE calculations (same as WizardAddEntity)
  const [fiscalParams, setFiscalParams] = useState({
    cfeHasLocal: true,
    cfeSurface: 50,
    cfeTarifM2: 15,
    cfeBaseMinimale: null,
    cfeTaux: 25,
    cvaeVAPercent: 30,
  });

  // Exercice overrides for manual CFE/CVAE values
  const [exerciceOverrides, setExerciceOverrides] = useState({});

  // Hypothesis editing mode
  const [editingHypotheses, setEditingHypotheses] = useState(false);

  // CFE base minimale calculation based on CA (barème 2026, valeurs médianes)
  // Source: https://entreprendre.service-public.gouv.fr/vosdroits/F23547
  // Les communes fixent la base dans une fourchette, on utilise la valeur médiane
  const getCfeBaseMinimaleFromCA = (ca) => {
    if (ca > 500000) return 3958;   // 247-7669 → médiane ~3958
    if (ca > 250000) return 3072;   // 247-5897 → médiane ~3072
    if (ca > 100000) return 2188;   // 247-4129 → médiane ~2188
    if (ca > 32600) return 1362;    // 247-2477 → médiane ~1362
    if (ca > 10000) return 713;     // 247-1179 → médiane ~713
    if (ca > 5000) return 418;      // 247-589 → médiane ~418
    return 247;                      // Minimum légal
  };

  // CFE base imposable
  const cfeBaseImposable = fiscalParams.cfeHasLocal
    ? fiscalParams.cfeSurface * fiscalParams.cfeTarifM2
    : (fiscalParams.cfeBaseMinimale || 0);

  // ==================== LOAD DATA ====================

  useEffect(() => {
    if (!entityId) { setLoading(false); return; }

    const loadData = async () => {
      try {
        // Use template API when in template mode, regular API otherwise
        const entity = mode === 'template'
          ? await apiService.getEntityTemplate(entityId)
          : await apiService.getEntity(entityId);
        console.log('[EntityEditPage] Loaded:', entity);

        // Identity
        setEntityData({
          name: entity.entity_name || entity.name || '',
          legalForm: entity.legal_form || entity.entity_type || 'SAS',
          taxRegime: entity.tax_regime || 'IS',
          activityType: entity.activity_type || 'BIC',
          creationDate: entity.creation_date ? entity.creation_date.substring(0, 10) : '',
          fiscalYearEnd: entity.fiscal_year_end || '31/12',
          shareCapital: entity.share_capital || '',
          firstExerciceType: entity.firstExerciceType || entity.first_exercice_type || 'long',
        });

        // Scenario ID and individuals for linking
        if (entity.scenario_id) {
          setScenarioId(entity.scenario_id);
          try {
            const individuals = await apiService.getIndividualsByScenario(entity.scenario_id);
            setScenarioIndividuals(individuals);
          } catch (e) {
            console.warn('[EntityEditPage] Could not load individuals:', e);
          }
        }

        // Managers - clear individualId for roles that were unlinked (before save)
        const loadedManagers = entity.managers || [];
        if (loadedManagers.length > 0) {
          setManagers(loadedManagers.map((m, i) => {
            const role = m.role || 'president';
            // If this role was cleared (relation removed), don't show individualId
            const wasCleared = clearedManagerRoles.includes(role?.toLowerCase());
            return {
              id: m.id || Date.now() + i,
              role,
              netMonthly: m.net_monthly || m.netMonthly || 0,
              months: m.months || 12,
              ownershipPercent: m.ownership_percent || m.ownershipPercent || null,
              status: m.status || 'ASSIMILE_SALARIE',
              individualId: wasCleared ? null : (m.individual_id || m.individualId || null),
              entryDate: m.entry_date || m.entryDate || null,
              salaryStartDate: m.salary_start_date || m.salaryStartDate || null,
            };
          }));
        } else {
          setManagers([{ id: 1, role: 'president', netMonthly: 0, months: 12, ownershipPercent: null, individualId: null, entryDate: null, salaryStartDate: null }]);
        }

        // Options
        setHasACRE(entity.has_acre || false);
        setHasCGA(entity.has_cga || false);
        setTvaRegime(entity.tva_regime || 'non_assujetti');
        setDividendPercent(entity.dividend_percent || 60);
        setDividendPaymentDelay(entity.dividend_payment_delay ?? 6);

        // Advanced options (parse from JSON if needed)
        const advOpts = entity.advanced_options
          ? (typeof entity.advanced_options === 'string' ? JSON.parse(entity.advanced_options) : entity.advanced_options)
          : {};
        setAdvancedOptions({
          jei: advOpts.jei || entity.is_jei || false,
          cir: advOpts.cir || false,
          cirAmount: advOpts.cirAmount || 0
        });

        // Employees
        setHasEmployees(entity.has_employees || false);
        setEmployeesData({
          count: entity.employees_count || 1,
          avgGrossSalary: entity.employees_avg_salary || 2500,
          months: entity.employees_months || 12
        });

        // Employee profiles
        if (entity.employee_profiles) {
          setEmployeeProfiles(Array.isArray(entity.employee_profiles) ? entity.employee_profiles : []);
        }

        // Charges
        const loadedChargesMode = entity.charges_mode || 'simplified';
        setChargeMode(loadedChargesMode);

        if (loadedChargesMode === 'detailed' && entity.charges_data) {
          setDetailedCharges(entity.charges_data);
        } else if (entity.charges) {
          const chargesData = typeof entity.charges === 'string' ? JSON.parse(entity.charges) : entity.charges;
          setSimplifiedCharges({
            totalPercent: chargesData.totalPercent || 20,
            totalFixed: chargesData.totalFixed || 25000,
            usePercent: chargesData.usePercent || false
          });
        }

        // Forecast dates - fallback to scenario dates if entity dates are missing
        let forecastStartDate = entity.forecast_start_date ? entity.forecast_start_date.substring(0, 10) : '';
        let forecastEndDate = entity.forecast_end_date ? entity.forecast_end_date.substring(0, 10) : '';

        // If entity has no forecast dates, try to get them from scenario
        if ((!forecastStartDate || !forecastEndDate) && entity.scenario_id) {
          try {
            const scenarios = await apiService.fetchScenariosWithDetails();
            const scenario = scenarios.find(s => s.id === entity.scenario_id);
            if (scenario) {
              if (!forecastStartDate && scenario.startDate) {
                forecastStartDate = scenario.startDate.substring(0, 10);
                console.log('[EntityEditPage] Using scenario startDate as fallback:', forecastStartDate);
              }
              if (!forecastEndDate && scenario.endDate) {
                forecastEndDate = scenario.endDate.substring(0, 10);
                console.log('[EntityEditPage] Using scenario endDate as fallback:', forecastEndDate);
              }
            }
          } catch (e) {
            console.warn('[EntityEditPage] Could not load scenario dates:', e);
          }
        }

        setForecastDates({
          startDate: forecastStartDate,
          endDate: forecastEndDate
        });

        // Exercice data (CA by year)
        if (entity.exercice_data) {
          setExerciceData(entity.exercice_data);
        }

      } catch (error) {
        console.error('Error loading entity:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, mode, JSON.stringify(clearedManagerRoles)]);

  // ==================== DERIVED ====================

  const fiscalYearEnd = entityData.fiscalYearEnd || '31/12';

  const managersWithStatus = useMemo(() => {
    const form = entityData.legalForm;
    if (form === 'SARL') {
      const totalParts = managers.reduce((sum, m) => sum + (m.ownershipPercent || 0), 0);
      const isCollectivelyMajority = totalParts > 50;
      return managers.map(m => ({
        ...m,
        status: isCollectivelyMajority ? 'TNS' : (m.ownershipPercent > 50 ? 'TNS' : 'ASSIMILE_SALARIE'),
        statusLabel: isCollectivelyMajority ? 'TNS' : (m.ownershipPercent > 50 ? 'TNS' : 'AS'),
      }));
    }
    const defaultStatus = getManagerTypeForLegalForm(form, true);
    const statusLabel = defaultStatus === 'TNS' ? 'TNS' : 'AS';
    return managers.map(m => ({ ...m, status: m.status || defaultStatus, statusLabel }));
  }, [managers, entityData.legalForm]);

  // Charges exploitation totales
  const totalExploitationCharges = useMemo(() => {
    if (chargeMode === 'detailed') {
      const fixed = Object.values(detailedCharges).reduce((sum, c) => {
        if (c.enabled && c.type === 'fixed') return sum + (c.value || 0);
        return sum;
      }, 0);
      return { fixed, percent: 0 };
    }
    return simplifiedCharges.usePercent
      ? { fixed: 0, percent: simplifiedCharges.totalPercent }
      : { fixed: simplifiedCharges.totalFixed, percent: 0 };
  }, [chargeMode, detailedCharges, simplifiedCharges]);

  // Generate exercices from forecast dates (respects creation date and firstExerciceType)
  const forecastExercicesData = useMemo(() => {
    if (!forecastDates.startDate || !forecastDates.endDate) {
      return { exercices: [], limitReached: false, lastGeneratedYear: null, requestedEndYear: null, skippedYears: 0 };
    }

    const parts = fiscalYearEnd.split('/');
    const fyDay = parts.length === 2 ? parseInt(parts[0]) : 31;
    const fyMonth = parts.length === 2 ? parseInt(parts[1]) : 12;

    const startDate = new Date(forecastDates.startDate);
    const endDate = new Date(forecastDates.endDate);
    const creationDate = entityData.creationDate ? new Date(entityData.creationDate) : null;
    const result = [];

    // Determine first exercice boundaries based on creation date and firstExerciceType
    let firstExerciceStart = creationDate || startDate;
    let firstExerciceEndYear = firstExerciceStart.getFullYear();

    // Calculate the next fiscal year end after creation date
    let nextFYEnd = new Date(firstExerciceEndYear, fyMonth - 1, fyDay);
    if (nextFYEnd <= firstExerciceStart) {
      nextFYEnd = new Date(firstExerciceEndYear + 1, fyMonth - 1, fyDay);
    }

    // Check if creation date is aligned with fiscal year start
    // Fiscal year START is the day after fiscal year END
    const fyStartDay = fyDay === 31 && fyMonth === 12 ? 1 : fyDay + 1;
    const fyStartMonth = fyDay === 31 && fyMonth === 12 ? 1 : (fyDay >= 28 ? fyMonth + 1 : fyMonth);
    const isAlignedWithFiscalYear =
      firstExerciceStart.getDate() === fyStartDay &&
      (firstExerciceStart.getMonth() + 1) === fyStartMonth;

    // For long first exercice, add one more year - BUT only if NOT aligned with fiscal year
    // When aligned (e.g., Jan 1 creation with Dec 31 fiscal year end), always use normal 12-month exercices
    const firstExerciceType = entityData.firstExerciceType || 'long';
    let firstExerciceEnd;
    if (isAlignedWithFiscalYear) {
      // Aligned: always 12-month exercice, ignore short/long setting
      firstExerciceEnd = nextFYEnd;
    } else {
      // Not aligned: apply short/long logic
      firstExerciceEnd = firstExerciceType === 'long'
        ? new Date(nextFYEnd.getFullYear() + 1, fyMonth - 1, fyDay)
        : nextFYEnd;
    }

    // Build exercices array
    let exerciceNumber = 1;
    let currentExerciceStart = firstExerciceStart;
    let currentExerciceEnd = firstExerciceEnd;
    const formatDate = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });

    while (result.length < 10) {
      // Check if exercice overlaps with forecast period
      if (currentExerciceEnd >= startDate && currentExerciceStart <= endDate) {
        result.push({
          year: currentExerciceEnd.getFullYear(),
          key: `${currentExerciceEnd.getFullYear()}`,
          label: `Exercice ${currentExerciceEnd.getFullYear()}`,
          startDate: formatDate(currentExerciceStart),
          endDate: formatDate(currentExerciceEnd),
          isFirstYear: exerciceNumber === 1,
          isSecondYear: exerciceNumber === 2,
          exerciceNumber,
        });
      }

      // Move to next exercice (always 12 months after first)
      if (currentExerciceStart > endDate) break;

      exerciceNumber++;
      currentExerciceStart = new Date(currentExerciceEnd);
      currentExerciceStart.setDate(currentExerciceStart.getDate() + 1);
      currentExerciceEnd = new Date(currentExerciceEnd.getFullYear() + 1, fyMonth - 1, fyDay);
    }

    // Check if we hit the 10 exercice limit while more years exist
    const limitReached = result.length >= 10 && currentExerciceStart <= endDate;
    const lastGeneratedYear = result.length > 0 ? result[result.length - 1].year : null;
    const requestedEndYear = endDate.getFullYear();

    return {
      exercices: result,
      limitReached,
      lastGeneratedYear,
      requestedEndYear,
      skippedYears: limitReached ? requestedEndYear - lastGeneratedYear : 0
    };
  }, [forecastDates, fiscalYearEnd, entityData.creationDate, entityData.firstExerciceType]);

  // Extract exercices array for backward compatibility
  const forecastExercices = forecastExercicesData.exercices || [];
  const exerciceLimitReached = forecastExercicesData.limitReached || false;
  const exerciceSkippedYears = forecastExercicesData.skippedYears || 0;

  // Get effective Step 3 mode
  const getStep3Mode = useCallback(() => step3Mode || chargeMode, [step3Mode, chargeMode]);

  const toggleStep3Mode = () => {
    const current = getStep3Mode();
    setStep3Mode(current === 'simplified' ? 'detailed' : 'simplified');
  };

  // Get exercice data for a year - includes per-year costs
  const getExerciceDataForYear = useCallback((yearKey) => {
    const defaultData = {
      // Revenue
      caPrincipal: { label: '', amount: 0, chargesVariables: { type: 'fixed', value: 0 } },
      caSecondaires: [],
      subventions: [],
      autresProduits: [],
      produitsFinanciers: { amount: 0 },
      // Per-year charges (fall back to global if not set)
      chargeMode: null,  // null = use global
      simplifiedCharges: null,
      detailedCharges: null,
      // Per-year managers (fall back to global if not set)
      managers: null,  // null = use global
      // Per-year employees (fall back to global if not set)
      hasEmployees: null,  // null = use global
      employeeProfiles: null,  // null = use global
    };
    return { ...defaultData, ...exerciceData[yearKey] };
  }, [exerciceData]);

  // Get effective charges for a year (per-year or global fallback)
  const getEffectiveCharges = useCallback((yearKey) => {
    const data = getExerciceDataForYear(yearKey);
    const mode = data.chargeMode ?? chargeMode;
    if (mode === 'detailed') {
      const charges = data.detailedCharges ?? detailedCharges;
      const fixed = Object.values(charges || {}).reduce((sum, c) => {
        if (c.enabled && c.type === 'fixed') return sum + (c.value || 0);
        return sum;
      }, 0);
      return { fixed, percent: 0 };
    }
    const simplified = data.simplifiedCharges ?? simplifiedCharges;
    return simplified.usePercent
      ? { fixed: 0, percent: simplified.totalPercent }
      : { fixed: simplified.totalFixed, percent: 0 };
  }, [getExerciceDataForYear, chargeMode, detailedCharges, simplifiedCharges]);

  // Get effective managers for a year (per-year or global fallback)
  const getEffectiveManagers = useCallback((yearKey) => {
    const data = getExerciceDataForYear(yearKey);
    const yearManagers = data.managers ?? managers;
    // Calculate status like managersWithStatus
    const form = entityData.legalForm;
    if (form === 'SARL' || form === 'EURL') {
      const totalOwnership = yearManagers.reduce((sum, m) => sum + (m.ownershipPercent || 0), 0);
      const isCollectivelyMajority = totalOwnership > 50;
      return yearManagers.map(m => ({
        ...m,
        status: m.status || (isCollectivelyMajority || (m.ownershipPercent || 0) > 50 ? 'TNS' : 'ASSIMILE_SALARIE'),
        statusLabel: isCollectivelyMajority ? 'TNS' : ((m.ownershipPercent || 0) > 50 ? 'TNS' : 'AS'),
      }));
    }
    const defaultStatus = getManagerTypeForLegalForm(form, true);
    const statusLabel = defaultStatus === 'TNS' ? 'TNS' : 'AS';
    return yearManagers.map(m => ({ ...m, status: m.status || defaultStatus, statusLabel }));
  }, [getExerciceDataForYear, managers, entityData.legalForm]);

  // Get effective employees for a year (per-year or global fallback)
  const getEffectiveEmployees = useCallback((yearKey) => {
    const data = getExerciceDataForYear(yearKey);
    return {
      hasEmployees: data.hasEmployees ?? hasEmployees,
      profiles: data.employeeProfiles ?? employeeProfiles,
      // Simplified mode data
      count: data.employeesCount ?? employeesData.count,
      avgGrossSalary: data.employeesAvgSalary ?? employeesData.avgGrossSalary,
      months: data.employeesMonths ?? employeesData.months,
    };
  }, [getExerciceDataForYear, hasEmployees, employeeProfiles, employeesData]);

  // Parse date from DD/MM/YY or DD/MM/YYYY format
  const parseFrenchDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return new Date(dateStr); // Fallback to native parsing
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    let year = parseInt(parts[2], 10);
    // Handle 2-digit years (00-99 → 2000-2099)
    if (year < 100) year += 2000;
    return new Date(year, month, day);
  }, []);

  // Format date as DD/MM/YY (like exercice line)
  const formatDateShort = useCallback((date) => {
    if (!date || isNaN(date.getTime())) return '';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }, []);

  // Calculate dividend payment date and tax year for an exercice
  const getDividendPaymentInfo = useCallback((exerciceKey, exerciceEndDate) => {
    // Get per-exercice override or use global delay
    const exerciceData = getExerciceDataForYear(exerciceKey);
    const delay = exerciceData.dividendPaymentDelay ?? dividendPaymentDelay;

    // Parse exercice end date (handles DD/MM/YY format)
    const endDate = parseFrenchDate(exerciceEndDate);

    // Handle invalid date
    if (!endDate || isNaN(endDate.getTime())) {
      return {
        delay,
        paymentDate: new Date(),
        taxYear: new Date().getFullYear(),
        paymentDateFormatted: 'Date invalide'
      };
    }

    // Calculate payment date (delay months after fiscal year end)
    const paymentDate = new Date(endDate);
    paymentDate.setMonth(paymentDate.getMonth() + delay);

    // Tax year is the year when dividends are paid
    const taxYear = paymentDate.getFullYear();

    return {
      delay,
      paymentDate,
      taxYear,
      paymentDateFormatted: paymentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    };
  }, [getExerciceDataForYear, dividendPaymentDelay, parseFrenchDate]);

  // Get dividend info for an exercice (with per-year overrides)
  const getDividendInfoForExercice = useCallback((exercice, resultatNet) => {
    const data = getExerciceDataForYear(exercice.key);

    // Utiliser override ou défaut global
    const effectivePercent = data.dividendPercentOverride !== null && data.dividendPercentOverride !== undefined
      ? data.dividendPercentOverride
      : dividendPercent;

    // Calcul du montant
    const dividendAmount = resultatNet > 0 ? Math.round(resultatNet * effectivePercent / 100) : 0;

    // Calcul de la date de versement
    let paymentDate = null;
    let paymentDateFormatted = '';

    if (data.dividendPaymentDateOverride) {
      paymentDate = new Date(data.dividendPaymentDateOverride);
      paymentDateFormatted = paymentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    } else {
      // Date par défaut: clôture + délai
      const endDate = parseFrenchDate(exercice.endDate);
      if (endDate && !isNaN(endDate.getTime())) {
        paymentDate = new Date(endDate);
        paymentDate.setMonth(paymentDate.getMonth() + dividendPaymentDelay);
        paymentDateFormatted = paymentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      }
    }

    const taxYear = paymentDate ? paymentDate.getFullYear() : null;

    return {
      percent: effectivePercent,
      isOverridden: data.dividendPercentOverride !== null || data.dividendPaymentDateOverride !== null,
      amount: dividendAmount,
      paymentDate,
      paymentDateFormatted,
      paymentDateOverride: data.dividendPaymentDateOverride,
      taxYear
    };
  }, [getExerciceDataForYear, dividendPercent, dividendPaymentDelay, parseFrenchDate]);

  // Update dividend percent for an exercice
  const updateDividendPercent = useCallback((exerciceKey, percent) => {
    setExerciceData(prev => ({
      ...prev,
      [exerciceKey]: {
        ...getExerciceDataForYear(exerciceKey),
        dividendPercentOverride: percent === dividendPercent ? null : percent
      }
    }));
  }, [getExerciceDataForYear, dividendPercent]);

  // Update dividend payment date for an exercice
  const updateDividendPaymentDate = useCallback((exerciceKey, date) => {
    setExerciceData(prev => ({
      ...prev,
      [exerciceKey]: {
        ...getExerciceDataForYear(exerciceKey),
        dividendPaymentDateOverride: date || null
      }
    }));
  }, [getExerciceDataForYear]);

  // Update AG date for an exercice
  const updateAGDate = useCallback((exerciceKey, date) => {
    setExerciceData(prev => ({
      ...prev,
      [exerciceKey]: {
        ...getExerciceDataForYear(exerciceKey),
        agDateOverride: date || null
      }
    }));
  }, [getExerciceDataForYear]);

  // Calculate affectation du résultat for an exercice
  // reportNMoins1 is the report à nouveau from previous exercice
  // cumulReserves is the cumulative réserve légale from previous exercices
  // explicitExerciceData can be passed to avoid closure staleness issues during save
  const getAffectationForExercice = useCallback((exercice, resultatNet, reportNMoins1 = 0, cumulReserves = 0, explicitExerciceData = null) => {
    const data = explicitExerciceData || getExerciceDataForYear(exercice.key);
    const capitalSocial = parseFloat(entityData.shareCapital) || 10000; // Default 10k€

    // Utiliser override ou défaut global pour le pourcentage de distribution
    const effectivePercent = data.dividendPercentOverride !== null && data.dividendPercentOverride !== undefined
      ? data.dividendPercentOverride
      : dividendPercent;

    // Check if legal reserve is required for this legal form
    const legalFormInfo = getLegalFormInfo(entityData.legalForm);
    const requiresLegalReserve = legalFormInfo?.requiresLegalReserve !== false; // Default to true if not specified

    // Réserve légale: 5% du résultat net, plafonné à 10% du capital - réserves cumulées
    // Only applies to commercial companies (SAS, SARL, SA, SELARL, HOLDING)
    // Civil companies (SCI, SNC) don't have this requirement
    const reserveLegalePlafond = requiresLegalReserve ? capitalSocial * 0.10 : 0;
    const reserveRestante = Math.max(0, reserveLegalePlafond - cumulReserves);
    const reserveLegaleCalculee = requiresLegalReserve && resultatNet > 0 ? Math.round(resultatNet * 0.05) : 0;
    const reserveLegale = Math.min(reserveLegaleCalculee, reserveRestante);
    const newCumulReserves = cumulReserves + reserveLegale;

    // Distribuable = Résultat Net - Réserve légale + Report N-1
    const distribuable = resultatNet - reserveLegale + reportNMoins1;

    // Dividendes = Distribuable × % de distribution (si distribuable > 0)
    const dividendes = distribuable > 0 ? Math.round(distribuable * effectivePercent / 100) : 0;

    // Report à nouveau = Distribuable - Dividendes
    const reportNouveau = distribuable - dividendes;

    // Date AG par défaut: 6 mois après clôture
    let agDate = null;
    if (data.agDateOverride) {
      agDate = new Date(data.agDateOverride);
    } else {
      const endDate = parseFrenchDate(exercice.endDate);
      if (endDate && !isNaN(endDate.getTime())) {
        agDate = new Date(endDate);
        agDate.setMonth(agDate.getMonth() + 6);
      }
    }

    // Date de versement
    let paymentDate = null;
    if (data.dividendPaymentDateOverride) {
      paymentDate = new Date(data.dividendPaymentDateOverride);
    } else if (agDate) {
      // Par défaut: AG + 15 jours
      paymentDate = new Date(agDate);
      paymentDate.setDate(paymentDate.getDate() + 15);
    }

    const taxYear = paymentDate ? paymentDate.getFullYear() : null;

    // SARL Warning: dividends > 10% of capital are subject to SSI charges for TNS gérant
    // This applies when entity is SARL and has a TNS manager (gérant majoritaire)
    let sarlWarning = null;
    if (entityData.legalForm === 'SARL' && dividendes > 0) {
      // Check if there's a TNS manager
      const hasTNSManager = managersWithStatus.some(m => m.status === 'TNS');
      if (hasTNSManager) {
        const seuilDividendes = capitalSocial * 0.10;
        const excedent = dividendes - seuilDividendes;
        if (excedent > 0) {
          const chargesSSIExcedent = Math.round(excedent * 0.45);
          sarlWarning = {
            seuil: seuilDividendes,
            excedent,
            chargesSSI: chargesSSIExcedent
          };
        }
      }
    }

    return {
      capitalSocial,
      resultatNet,
      requiresLegalReserve,
      reserveLegale,
      reserveLegalePlafond,
      reserveRestante,
      cumulReserves,
      newCumulReserves,
      reportNMoins1,
      distribuable,
      percent: effectivePercent,
      dividendes,
      reportNouveau,
      agDate,
      agDateFormatted: formatDateShort(agDate),
      agDateOverride: data.agDateOverride,
      paymentDate,
      paymentDateFormatted: formatDateShort(paymentDate),
      paymentDateOverride: data.dividendPaymentDateOverride,
      taxYear,
      sarlWarning,
      isOverridden: data.dividendPercentOverride !== null || data.agDateOverride !== null || data.dividendPaymentDateOverride !== null
    };
  }, [getExerciceDataForYear, entityData.shareCapital, entityData.legalForm, dividendPercent, parseFrenchDate, formatDateShort, managersWithStatus]);

  // Calculate totals for an exercice
  // Optional explicitForecastDates param allows passing current dates explicitly (for save)
  const calculateExerciceTotals = useCallback((exercice, explicitForecastDates = null) => {
    const effectiveForecastDates = explicitForecastDates || forecastDates;
    const data = getExerciceDataForYear(exercice.key);
    const ca = data.caPrincipal?.amount || 0;

    // Charges variables
    const chargesVar = data.caPrincipal?.chargesVariables?.type === 'percent'
      ? ca * (data.caPrincipal?.chargesVariables?.value || 0) / 100
      : (data.caPrincipal?.chargesVariables?.value || 0);

    // CA secondaires
    const caSecondaires = (data.caSecondaires || []).reduce((sum, s) => sum + (s.amount || 0), 0);

    // Subventions - handle both array and object formats
    const subventions = Array.isArray(data.subventions)
      ? data.subventions.reduce((sum, s) => sum + (s.amount || 0), 0)
      : (data.subventions?.amount || 0);

    // Autres produits - handle both array and object formats
    const autresProduits = Array.isArray(data.autresProduits)
      ? data.autresProduits.reduce((sum, s) => sum + (s.amount || 0), 0)
      : (data.autresProduits?.amount || 0);

    // Produits financiers
    const produitsFinanciers = data.produitsFinanciers?.amount || 0;

    const totalProduits = ca + caSecondaires + subventions + autresProduits + produitsFinanciers;

    // Charges exploitation - PER YEAR (proratisées après calcul du durationFactor)
    const yearCharges = getEffectiveCharges(exercice.key);
    // Note: chargesExploitation sera calculé après exerciceDurationFactor

    // Calculate proration factor based on a start date vs exercice dates
    const calculateProrationForDate = (startDateInput) => {
      const parseExerciceDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split('/');
        if (parts.length !== 3) return null;
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const year = parseInt(parts[2]) + (parseInt(parts[2]) < 50 ? 2000 : 1900);
        return new Date(year, month, day);
      };
      const exerciceStart = parseExerciceDate(exercice.startDate);
      const exerciceEnd = parseExerciceDate(exercice.endDate);
      const startDate = startDateInput ? new Date(startDateInput) : null;
      if (!exerciceStart || !exerciceEnd || !startDate) return 1;
      if (startDate > exerciceEnd) return 0;
      if (startDate <= exerciceStart) return 1;
      const totalDays = Math.round((exerciceEnd - exerciceStart) / (24 * 60 * 60 * 1000)) + 1;
      const activeDays = Math.round((exerciceEnd - startDate) / (24 * 60 * 60 * 1000)) + 1;
      return activeDays / totalDays;
    };
    // Entity-level proration (for employees)
    const prorataFactor = calculateProrationForDate(entityData.creationDate);

    // Calculate exercice duration factor (how many 12-month periods this exercice represents)
    const parseExerciceDateForDuration = (dateStr) => {
      if (!dateStr) return null;
      const parts = dateStr.split('/');
      if (parts.length !== 3) return null;
      const day = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const year = parseInt(parts[2]) + (parseInt(parts[2]) < 50 ? 2000 : 1900);
      return new Date(year, month, day);
    };
    const exerciceStartDate = parseExerciceDateForDuration(exercice.startDate);
    const exerciceEndDate = parseExerciceDateForDuration(exercice.endDate);
    let exerciceDurationFactor = 1;
    if (exerciceStartDate && exerciceEndDate) {
      // Clip to forecast period - exercice duration is bounded by forecast start/end dates
      const forecastStart = effectiveForecastDates.startDate ? new Date(effectiveForecastDates.startDate) : null;
      const forecastEnd = effectiveForecastDates.endDate ? new Date(effectiveForecastDates.endDate) : null;
      const effectiveStartDate = (forecastStart && exerciceStartDate < forecastStart) ? forecastStart : exerciceStartDate;
      const effectiveEndDate = (forecastEnd && exerciceEndDate > forecastEnd) ? forecastEnd : exerciceEndDate;

      // Guard against exercice being entirely outside forecast period
      if (effectiveEndDate < effectiveStartDate) {
        exerciceDurationFactor = 0;
      } else {
        const totalDays = Math.round((effectiveEndDate - effectiveStartDate) / (24 * 60 * 60 * 1000)) + 1;
        const durationMonths = totalDays / 30.4375; // Average days per month (365.25/12)
        // Round to avoid floating point issues: 11.9-12.1 months = factor 1.0
        exerciceDurationFactor = (durationMonths >= 11.9 && durationMonths <= 12.1) ? 1 : Math.max(0, durationMonths / 12);
      }
    }

    // Dirigeants - PER YEAR with ACRE on charges only and PER-MANAGER PRORATION
    const yearManagers = getEffectiveManagers(exercice.key);
    // Calculate per-manager data with BRUT, NET, charges - this will be stored in exercice_data
    const managersCalculated = yearManagers.map(m => {
      // Use salaryStartDate if available, then entryDate, then entity creation date
      const managerProrata = calculateProrationForDate(m.salaryStartDate || m.entryDate || entityData.creationDate);

      // Calcul du salaire NET pour la durée EFFECTIVE de l'exercice
      // netMonthly = ce que le dirigeant reçoit réellement
      // months = 12, 13 ou 14 (option de versement, pas un multiplicateur direct)
      const effectiveMonths = 12 * exerciceDurationFactor * managerProrata;
      const baseSalary = (m.netMonthly || 0) * effectiveMonths;

      // Prime 13ème/14ème mois (proratisée sur la durée effective)
      const bonusMonths = Math.max(0, (m.months || 12) - 12); // 0, 1 ou 2 mois de prime
      const bonusSalary = (m.netMonthly || 0) * bonusMonths * exerciceDurationFactor * managerProrata;

      const annualNet = Math.round(baseSalary + bonusSalary);
      // Use calculateChargesFromNet since wizard collects NET salary (what manager receives)
      const calc = calculateChargesFromNet(annualNet, m.status);

      // ACRE: réduction de 50% sur les charges
      let finalBrut = calc.brut;
      let finalNet = calc.net;
      let chargesSalariales = calc.chargesSalariales;
      let chargesPatronales = calc.chargesPatronales;
      let coutEmployeur = calc.cout;

      if (hasACRE && exercice.isFirstYear) {
        // ACRE reduces charges by 50%
        chargesSalariales = Math.round(calc.chargesSalariales * 0.5);
        chargesPatronales = Math.round(calc.chargesPatronales * 0.5);
        // For AS: net increases because charges salariales decrease
        if (m.status === 'AS' || m.status === 'ASSIMILE_SALARIE') {
          finalNet = finalBrut - chargesSalariales;
        }
        coutEmployeur = finalBrut + chargesPatronales;
      }

      return {
        id: m.id,
        role: m.role,
        status: m.status,
        brut: finalBrut,
        chargesSalariales,
        net: finalNet,
        chargesPatronales,
        coutEmployeur,
        prorata: managerProrata,
        durationFactor: exerciceDurationFactor
      };
    });
    const coutDirigeants = managersCalculated.reduce((sum, m) => sum + m.coutEmployeur, 0);

    // Employés - PER YEAR with PRORATION and EXERCICE DURATION
    const yearEmployees = getEffectiveEmployees(exercice.key);
    const yearChargeMode = data.chargeMode ?? chargeMode;
    let coutEmployes = 0;
    if (yearEmployees.hasEmployees) {
      if (yearChargeMode === 'detailed' && yearEmployees.profiles.length > 0) {
        coutEmployes = yearEmployees.profiles.reduce((sum, p) => {
          // Calcul du salaire pour la durée EFFECTIVE
          // months = 12, 13 ou 14 (option de versement)
          const effectiveMonths = 12 * exerciceDurationFactor * prorataFactor;
          const baseSalary = p.grossMonthlySalary * effectiveMonths * p.headcount;
          // Prime 13ème/14ème mois proratisée
          const bonusMonths = Math.max(0, (p.months || 12) - 12);
          const bonusSalary = p.grossMonthlySalary * bonusMonths * exerciceDurationFactor * prorataFactor * p.headcount;
          const annualBrut = Math.round(baseSalary + bonusSalary);
          return sum + Math.round(annualBrut * 1.45);
        }, 0);
      } else {
        // Mode simplifié - même logique
        const effectiveMonths = 12 * exerciceDurationFactor * prorataFactor;
        const baseSalary = yearEmployees.count * yearEmployees.avgGrossSalary * effectiveMonths;
        // Prime 13ème/14ème mois proratisée
        const bonusMonths = Math.max(0, (yearEmployees.months || 12) - 12);
        const bonusSalary = yearEmployees.count * yearEmployees.avgGrossSalary * bonusMonths * exerciceDurationFactor * prorataFactor;
        const annualBrut = Math.round(baseSalary + bonusSalary);
        coutEmployes = Math.round(annualBrut * 1.45);
      }
    }

    // Charges exploitation - proratisées selon durée effective de l'exercice
    // Les charges fixes annuelles (25 000€/an) deviennent 12 500€ pour un exercice de 6 mois
    const chargesExploitationBase = yearCharges.fixed || (ca * yearCharges.percent / 100);
    const chargesExploitation = yearCharges.fixed
      ? Math.round(chargesExploitationBase * exerciceDurationFactor) // Charges fixes proratisées
      : chargesExploitationBase; // Charges en % du CA déjà proportionnelles

    // CFE calculation (same as WizardAddEntity)
    const baseMinAuto = getCfeBaseMinimaleFromCA(ca);
    const baseMinEffective = fiscalParams.cfeBaseMinimale || baseMinAuto;
    const cotisationMin = Math.round(baseMinEffective * fiscalParams.cfeTaux / 100);

    let cfeBase = 0;
    let cfeCalculee = 0;
    if (fiscalParams.cfeHasLocal) {
      cfeBase = fiscalParams.cfeSurface * fiscalParams.cfeTarifM2;
      const cfeFromLocal = Math.round(cfeBase * fiscalParams.cfeTaux / 100);
      cfeCalculee = cfeFromLocal < cotisationMin ? cotisationMin : cfeFromLocal;
    } else {
      cfeBase = baseMinEffective;
      cfeCalculee = cotisationMin;
    }

    let cfeAuto = cfeCalculee;
    if (exercice.isFirstYear) cfeAuto = 0; // Exo 100% année 1
    else if (exercice.isSecondYear) cfeAuto = Math.round(cfeCalculee * 0.5); // -50% année 2

    const cfe = exerciceOverrides[exercice.key]?.cfe ?? cfeAuto;

    // CVAE calculation
    let cvaeAuto = 0;
    if (totalProduits >= 500000) {
      const va = totalProduits * fiscalParams.cvaeVAPercent / 100;
      if (totalProduits < 3000000) cvaeAuto = Math.round(va * 0.0025);
      else if (totalProduits < 10000000) cvaeAuto = Math.round(va * 0.005);
      else if (totalProduits < 50000000) cvaeAuto = Math.round(va * 0.01);
      else cvaeAuto = Math.round(va * 0.015);
    }
    const cvae = exerciceOverrides[exercice.key]?.cvae ?? cvaeAuto;

    const totalImpotsEtTaxes = cfe + cvae;
    const chargesGlobales = chargesVar + chargesExploitation + coutDirigeants + coutEmployes + totalImpotsEtTaxes;
    const resultatAvantIS = totalProduits - chargesGlobales;

    // IS (simplifié 15%/25%)
    let isAmount = 0;
    if (resultatAvantIS > 0) {
      if (resultatAvantIS <= 42500) {
        isAmount = Math.round(resultatAvantIS * 0.15);
      } else {
        isAmount = Math.round(42500 * 0.15 + (resultatAvantIS - 42500) * 0.25);
      }
    }

    const resultatNet = resultatAvantIS - isAmount;

    return {
      totalProduits,
      totalCA: ca,
      totalChargesVar: chargesVar,
      chargesExploitation,
      coutDirigeants,
      coutEmployes,
      totalImpotsEtTaxes,
      cfe,
      cvae,
      chargesGlobales,
      resultatAvantIS,
      isAmount,
      resultatNet,
      managersCalculated  // Per-manager calculated data: brut, net, charges for PersonEditPage
    };
  }, [getExerciceDataForYear, getEffectiveCharges, getEffectiveManagers, getEffectiveEmployees, hasACRE, chargeMode, employeesData, fiscalParams, exerciceOverrides, entityData.creationDate, forecastDates]);

  // ==================== HANDLERS ====================

  const handleManagerChange = (managerId, field, value) => {
    setManagers(prev => prev.map(m => m.id === managerId ? { ...m, [field]: value } : m));
  };

  // Per-year manager update - each exercice has independent salary configuration
  const handleManagerChangeForYear = (yearKey, managerId, field, value) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      // If no per-year managers yet, copy from global as starting point
      const yearManagers = data.managers ?? [...managers];
      return {
        ...prev,
        [yearKey]: {
          ...data,
          managers: yearManagers.map(m => m.id === managerId ? { ...m, [field]: value } : m)
        }
      };
    });
    // NOTE: We intentionally do NOT sync most fields to global managers
    // Each exercice should have independent salary settings
    // BUT: individualId and netMonthly should be synced to global managers for income auto-generation
    // (netMonthly sync uses the max value across years for the global baseline)
    if (field === 'individualId' || field === 'netMonthly') {
      setManagers(prev => prev.map(m => {
        if (m.id !== managerId) return m;
        if (field === 'individualId') {
          return { ...m, individualId: value };
        }
        // For netMonthly, update if new value is higher (use max salary for income generation)
        const newNetMonthly = field === 'netMonthly' ? value : m.netMonthly;
        return { ...m, netMonthly: Math.max(m.netMonthly || 0, newNetMonthly || 0) };
      }));
    }
  };

  // Per-year employee profile update
  const updateEmployeeProfileForYear = (yearKey, profileId, field, value) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      // If no per-year profiles yet, copy from global
      const yearProfiles = data.employeeProfiles ?? [...employeeProfiles];
      return {
        ...prev,
        [yearKey]: {
          ...data,
          employeeProfiles: yearProfiles.map(p => p.id === profileId ? { ...p, [field]: value } : p)
        }
      };
    });
  };

  // Per-year add/remove employee profile
  const addEmployeeProfileForYear = (yearKey) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      const yearProfiles = data.employeeProfiles ?? [...employeeProfiles];
      return {
        ...prev,
        [yearKey]: {
          ...data,
          employeeProfiles: [...yearProfiles, { id: Date.now(), profileName: '', grossMonthlySalary: 2500, months: 12, headcount: 1 }]
        }
      };
    });
  };

  const removeEmployeeProfileForYear = (yearKey, profileId) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      const yearProfiles = data.employeeProfiles ?? [...employeeProfiles];
      return {
        ...prev,
        [yearKey]: {
          ...data,
          employeeProfiles: yearProfiles.filter(p => p.id !== profileId)
        }
      };
    });
  };

  // Per-year charges update (simplified mode)
  const updateSimplifiedChargesForYear = (yearKey, updates) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      const yearSimplified = data.simplifiedCharges ?? { ...simplifiedCharges };
      return {
        ...prev,
        [yearKey]: {
          ...data,
          simplifiedCharges: { ...yearSimplified, ...updates }
        }
      };
    });
  };

  // Per-year hasEmployees toggle
  const setHasEmployeesForYear = (yearKey, value) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      return {
        ...prev,
        [yearKey]: {
          ...data,
          hasEmployees: value,
          // Initialize profiles from global if enabling
          employeeProfiles: value && !data.employeeProfiles ? [...employeeProfiles] : data.employeeProfiles
        }
      };
    });
  };

  // Per-year update simplified employees aggregated data
  const updateEmployeesAggregatedForYear = (yearKey, updates) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      return {
        ...prev,
        [yearKey]: {
          ...data,
          employeesCount: updates.count !== undefined ? updates.count : (data.employeesCount ?? employeesData.count),
          employeesAvgSalary: updates.avgGrossSalary !== undefined ? updates.avgGrossSalary : (data.employeesAvgSalary ?? employeesData.avgGrossSalary),
          employeesMonths: updates.months !== undefined ? updates.months : (data.employeesMonths ?? employeesData.months),
        }
      };
    });
  };

  // Per-year add manager
  const addManagerForYear = (yearKey) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      const yearManagers = data.managers ?? [...managers];
      const form = entityData.legalForm;
      const defaultRole = form === 'SARL' || form === 'EURL' ? 'co-gerant' : 'dg';
      const defaultStatus = getManagerTypeForLegalForm(form, true);
      const newManager = {
        id: Date.now(),
        role: defaultRole,
        netMonthly: 2500,
        months: 12,
        ownershipPercent: 0,
        individualId: null,
        status: defaultStatus
      };
      return {
        ...prev,
        [yearKey]: {
          ...data,
          managers: [...yearManagers, newManager]
        }
      };
    });
  };

  // Per-year remove manager
  const removeManagerForYear = (yearKey, managerId) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      const yearManagers = data.managers ?? [...managers];
      return {
        ...prev,
        [yearKey]: {
          ...data,
          managers: yearManagers.filter(m => m.id !== managerId)
        }
      };
    });
  };

  const updateCAPrincipal = (yearKey, updates) => {
    setExerciceData(prev => ({
      ...prev,
      [yearKey]: {
        ...getExerciceDataForYear(yearKey),
        caPrincipal: { ...getExerciceDataForYear(yearKey).caPrincipal, ...updates }
      }
    }));
  };

  const getRoleLabel = (role) => {
    const labels = { 'president': 'Président', 'dg': 'DG', 'gerant': 'Gérant', 'co-gerant': 'Co-gérant' };
    return labels[role] || role;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount || 0);

  // Employee profile management
  const addEmployeeProfile = () => {
    setEmployeeProfiles(prev => [
      ...prev,
      { id: Date.now(), profileName: '', grossMonthlySalary: 2500, months: 12, headcount: 1 }
    ]);
  };

  const removeEmployeeProfile = (id) => {
    setEmployeeProfiles(prev => prev.filter(p => p.id !== id));
  };

  const updateEmployeeProfile = (id, field, value) => {
    setEmployeeProfiles(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  // Update CA Secondaire for modal editing
  const updateCASecondaire = (yearKey, sourceId, updates) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(yearKey);
      return {
        ...prev,
        [yearKey]: {
          ...data,
          caSecondaires: (data.caSecondaires || []).map(s =>
            s.id === sourceId ? { ...s, ...updates } : s
          )
        }
      };
    });
  };

  // Ajouter une source de CA secondaire
  const addCASecondaire = useCallback((exerciceKey) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      const newSecondaire = {
        id: Date.now(),
        label: `Activité secondaire ${(data.caSecondaires || []).length + 1}`,
        amount: 0,
        chargesVariables: { type: 'fixed', value: 0 }
      };
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          caSecondaires: [...(data.caSecondaires || []), newSecondaire]
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Supprimer une source de CA secondaire
  const removeCASecondaire = useCallback((exerciceKey, sourceId) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          caSecondaires: (data.caSecondaires || []).filter(s => s.id !== sourceId)
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Ajouter une subvention
  const addSubvention = useCallback((exerciceKey) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      const newSubvention = {
        id: Date.now(),
        label: 'Subvention',
        amount: 0,
        organisme: ''
      };
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          subventions: [...(data.subventions || []), newSubvention]
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Ajouter un autre produit
  const addAutreProduit = useCallback((exerciceKey) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      const newProduit = {
        id: Date.now(),
        label: 'Autre produit',
        amount: 0
      };
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          autresProduits: [...(data.autresProduits || []), newProduit]
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Mettre à jour une subvention
  const updateSubvention = useCallback((exerciceKey, subId, updates) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          subventions: (data.subventions || []).map(s =>
            s.id === subId ? { ...s, ...updates } : s
          )
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Supprimer une subvention
  const removeSubvention = useCallback((exerciceKey, subId) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          subventions: (data.subventions || []).filter(s => s.id !== subId)
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Mettre à jour un autre produit
  const updateAutreProduit = useCallback((exerciceKey, prodId, updates) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          autresProduits: (data.autresProduits || []).map(p =>
            p.id === prodId ? { ...p, ...updates } : p
          )
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Supprimer un autre produit
  const removeAutreProduit = useCallback((exerciceKey, prodId) => {
    setExerciceData(prev => {
      const data = getExerciceDataForYear(exerciceKey);
      return {
        ...prev,
        [exerciceKey]: {
          ...data,
          autresProduits: (data.autresProduits || []).filter(p => p.id !== prodId)
        }
      };
    });
  }, [getExerciceDataForYear]);

  // Manager add function (for modal)
  const addManager = () => {
    setManagers(prev => [
      ...prev,
      { id: Date.now(), role: 'dg', netMonthly: 0, months: 12, ownershipPercent: null, individualId: null }
    ]);
  };

  // Check if can add more managers based on legal form
  const canAddManager = useMemo(() => {
    const form = entityData.legalForm;
    if (form === 'EI' || form === 'EIRL') return false;
    if (form === 'SAS' && managers.length >= 2) return false; // Max 2: Président + DG
    return true;
  }, [entityData.legalForm, managers.length]);

  const addManagerLabel = useMemo(() => {
    const form = entityData.legalForm;
    if (form === 'SARL' && managers.length === 0) return 'Ajouter un gérant';
    if (form === 'SARL' && managers.length > 0) return 'Ajouter un co-gérant';
    if (form === 'SAS' && managers.length === 0) return 'Ajouter un président';
    if (form === 'SAS' && managers.length === 1) return 'Ajouter un DG';
    return 'Ajouter un dirigeant';
  }, [entityData.legalForm, managers.length]);

  // ==================== SAVE ====================

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Enrich exerciceData with calculated data for each exercice
      // Use ref to get the latest exerciceData (avoids stale closure issue)
      const enrichedExerciceData = { ...exerciceDataRef.current };
      // Use ref to get the latest forecastDates (avoids stale closure issue)
      const currentForecastDates = forecastDatesRef.current;

      // Calculate affectations with running report N-1 and cumulative reserves (same logic as display)
      let runningReport = 0;
      let runningReserves = 0;
      for (const exercice of forecastExercices) {
        // Pass forecastDates explicitly to ensure we use the current values
        const totals = calculateExerciceTotals(exercice, currentForecastDates);

        // Get the current exercice data (from enriched, which was spread from state)
        const currentExerciceData = enrichedExerciceData[exercice.key] || {};

        // Use getAffectationForExercice for accurate dividend calculation
        // Pass explicit exercice data to avoid closure staleness issues
        const affectation = getAffectationForExercice(exercice, totals.resultatNet, runningReport, runningReserves, currentExerciceData);
        runningReport = affectation.reportNouveau;
        runningReserves = affectation.newCumulReserves;

        // Parse end date for ISO string
        const parsedEndDate = parseFrenchDate(exercice.endDate);

        enrichedExerciceData[exercice.key] = {
          ...enrichedExerciceData[exercice.key],
          managersCalculated: totals.managersCalculated,
          // Dividend data for server-side income generation
          resultatNet: totals.resultatNet,
          dividendAmount: affectation.dividendes,
          dividendPaymentDate: affectation.paymentDate ? affectation.paymentDate.toISOString() : null,
          dividendTaxYear: affectation.taxYear,
          exerciceEndDate: parsedEndDate ? parsedEndDate.toISOString() : exercice.endDate
        };
      }

      const data = {
        // Hypotheses that affect exercice generation
        creation_date: entityData.creationDate,
        fiscal_year_end: entityData.fiscalYearEnd,
        first_exercice_type: entityData.firstExerciceType || 'long',
        // Managers
        managers: JSON.stringify(managersWithStatus.map(m => ({
          role: m.role, net_monthly: m.netMonthly, months: m.months,
          ownership_percent: m.ownershipPercent, status: m.status,
          individual_id: m.individualId || null,
          entryDate: m.entryDate || null,
          salaryStartDate: m.salaryStartDate || null,
        }))),
        has_acre: hasACRE,
        has_cga: hasCGA,
        tva_regime: tvaRegime,
        dividend_percent: dividendPercent,
        dividend_payment_delay: dividendPaymentDelay,
        advanced_options: JSON.stringify(advancedOptions),
        has_employees: hasEmployees,
        employees_count: hasEmployees ? employeesData.count : 0,
        employees_avg_salary: hasEmployees ? employeesData.avgGrossSalary : 0,
        employees_months: hasEmployees ? employeesData.months : 12,
        employee_profiles: JSON.stringify(employeeProfiles),
        charges_mode: chargeMode,
        charges_data: chargeMode === 'simplified' ? JSON.stringify(simplifiedCharges) : JSON.stringify(detailedCharges),
        forecast_start_date: currentForecastDates.startDate,
        forecast_end_date: currentForecastDates.endDate,
        exercice_data: JSON.stringify(enrichedExerciceData),
      };

      // Use template API when in template mode, regular API otherwise
      if (mode === 'template') {
        await apiService.updateEntityTemplate(entityId, data);
      } else {
        await apiService.updateEntity(entityId, data);
      }
      if (onSave) onSave(data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div className={`entity-edit-page ${standalone ? 'standalone' : ''}`}>
        <div className="edit-topbar">
          <h3>Chargement...</h3>
        </div>
      </div>
    );
  }

  const hasEndDate = forecastDates.endDate;
  const isDetailedMode = getStep3Mode() === 'detailed';

  // Summary of hypotheses - avec prise en compte ACRE
  const step2Summary = {
    dirigeants: managersWithStatus.length,
    coutDirigeantsAnnuel: managersWithStatus.reduce((sum, m) => {
      const annualNet = m.netMonthly * m.months;
      const baseCalc = calculateChargesFromNet(annualNet, m.status);
      // ACRE: -50% sur les charges patronales uniquement
      const charges = hasACRE ? Math.round(baseCalc.chargesPatronales * 0.5) : baseCalc.chargesPatronales;
      return sum + baseCalc.brut + charges;
    }, 0),
    hasEmployees,
    coutEmployesAnnuel: hasEmployees ? Math.round(employeesData.count * employeesData.avgGrossSalary * employeesData.months * 1.45) : 0,
    chargesExploitation: totalExploitationCharges
  };

  return (
    <div className={`entity-edit-page ${standalone ? 'standalone' : ''}`}>
      {/* HEADER - Identity summary */}
      <div className="edit-topbar">
        <div className="topbar-left">
          <span className="legal-badge">{entityData.legalForm}</span>
          <h3>{mode === 'template' ? 'Template: ' : ''}{entityData.name}</h3>
          <span className="tax-badge">{entityData.taxRegime}</span>
          <span className="activity-badge">{entityData.activityType}</span>
          {standalone && (
            <span className="standalone-badge">Page complète</span>
          )}
        </div>
        <div className="topbar-right">
          <button
            className={`save-btn ${isSaving ? 'saving' : ''} ${saveSuccess ? 'success' : ''}`}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Enregistrement...' : saveSuccess ? 'Enregistré !' : 'Enregistrer'}
          </button>
          <button
            className="close-btn"
            onClick={onClose}
            title={standalone ? "Retour à l'accueil" : "Fermer"}
          >
            {standalone ? '← Retour' : '×'}
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="edit-body">
        <div className="wizard-add-entity">
        <div className="step-content ca-step">
          {/* Warning when 10 exercice limit is reached - displayed at top */}
          {exerciceLimitReached && (
            <div className="exercice-limit-warning">
              <span className="warning-icon">⚠️</span>
              <span>Limite de 10 exercices atteinte. Les années {forecastExercicesData.lastGeneratedYear + 1} à {forecastExercicesData.requestedEndYear} ne seront pas calculées.</span>
            </div>
          )}
          {/* Résumé hypothèses Step 2 - Clickable pour éditer */}
          <div className="hypotheses-section-title">Hypothèses</div>
          <div
            className={`step2-summary ${editingHypotheses ? 'editing' : ''}`}
            onClick={() => !editingHypotheses && setEditingHypotheses(true)}
            style={{ cursor: editingHypotheses ? 'default' : 'pointer' }}
          >
            {!editingHypotheses ? (
              <button
                type="button"
                className="hyp-action-btn modifier"
                onClick={(e) => { e.stopPropagation(); setEditingHypotheses(true); }}
                title="Modifier les hypothèses"
              >
                <span className="btn-icon">✎</span>
                <span className="btn-text">Modifier</span>
              </button>
            ) : (
              <div className="hyp-action-buttons">
                <button
                  type="button"
                  className="hyp-action-btn appliquer"
                  onClick={(e) => { e.stopPropagation(); setEditingHypotheses(false); }}
                  title="Appliquer les modifications"
                >
                  Appliquer
                </button>
                <button
                  type="button"
                  className="hyp-action-btn close"
                  onClick={(e) => { e.stopPropagation(); setEditingHypotheses(false); }}
                  title="Fermer sans appliquer"
                >
                  ×
                </button>
              </div>
            )}

            {!editingHypotheses ? (
              /* Mode lecture - compact 2 lines */
              <div className="summary-content-compact">
                <span className="summary-item">
                  <strong>{step2Summary.dirigeants}</strong> dir.
                  <span className={`status-badge ${managersWithStatus.some(m => m.status === 'TNS') ? 'tns' : 'as'}`}>
                    {managersWithStatus.length > 0 && (managersWithStatus.every(m => m.status === 'TNS') ? 'TNS' : managersWithStatus.every(m => m.status === 'AS') ? 'AS' : 'Mix')}
                  </span>
                </span>
                {step2Summary.hasEmployees && (
                  <span className="summary-item"><strong>{employeesData.count}</strong> emp.</span>
                )}
                <span className="summary-item">Charges: <em>{step2Summary.chargesExploitation.fixed ? formatCurrency(step2Summary.chargesExploitation.fixed) : `${step2Summary.chargesExploitation.percent}%`}</em></span>
                <span className="summary-item">Capital: <em>{entityData.shareCapital ? formatCurrency(parseFloat(entityData.shareCapital)) : '10 000 €'}</em></span>
                <span className="summary-item">Clôture: <em>{entityData.fiscalYearEnd || '31/12'}</em></span>
                {hasACRE && <span className="summary-tag">ACRE</span>}
                {advancedOptions.jei && <span className="summary-tag jei">JEI</span>}
                {tvaRegime !== 'non_assujetti' && <span className="summary-tag tva">{tvaRegime === 'reel_simplifie' ? 'TVA Simp.' : 'TVA'}</span>}
              </div>
            ) : (
              /* Mode édition */
              <div className="hypotheses-edit-form" onClick={(e) => e.stopPropagation()}>
                {/* Row 1: Dates + Dividendes */}
                <div className="hyp-row">
                  <div className="edit-section">
                    <h4>Dates</h4>
                    <div className="dates-edit-row">
                      <label>Création</label>
                      <input
                        type="date"
                        value={entityData.creationDate || ''}
                        onChange={(e) => setEntityData(prev => ({ ...prev, creationDate: e.target.value }))}
                        className="date-input"
                      />
                      <span className="separator">|</span>
                      <label>Clôture</label>
                      <select
                        value={entityData.fiscalYearEnd || '31/12'}
                        onChange={(e) => setEntityData(prev => ({ ...prev, fiscalYearEnd: e.target.value }))}
                        className="cloture-select"
                      >
                        <option value="31/03">31 mars</option>
                        <option value="30/06">30 juin</option>
                        <option value="30/09">30 septembre</option>
                        <option value="31/12">31 décembre</option>
                      </select>
                      {entityData.fiscalYearEnd && entityData.fiscalYearEnd !== '31/12' && (
                        <>
                          <span className="separator">|</span>
                          <label>1er exercice</label>
                          <select
                            value={entityData.firstExerciceType || 'long'}
                            onChange={(e) => setEntityData(prev => ({ ...prev, firstExerciceType: e.target.value }))}
                            className="first-exercice-select"
                          >
                            <option value="short">Court</option>
                            <option value="long">Long</option>
                          </select>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="edit-section">
                    <h4>Capital Social</h4>
                    <div className="capital-row">
                      <input
                        type="number"
                        value={entityData.shareCapital || ''}
                        onChange={(e) => setEntityData(prev => ({ ...prev, shareCapital: e.target.value }))}
                        placeholder="10000"
                        className="medium-input"
                      />
                      <span className="unit">€</span>
                      <Tooltip text="Le capital social détermine le plafond de la réserve légale (10% du capital)">
                        <span className="help-icon">ⓘ</span>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="edit-section">
                    <h4>Dividendes</h4>
                    <div className="dividend-row">
                      <label>Distribution</label>
                      <input
                        type="number"
                        value={dividendPercent}
                        onChange={(e) => setDividendPercent(parseFloat(e.target.value) || 0)}
                        min="0"
                        max="100"
                        className="small-input"
                      />
                      <span className="unit">%</span>
                    </div>
                    <div className="dividend-row">
                      <label>Versement</label>
                      <input
                        type="number"
                        value={dividendPaymentDelay}
                        onChange={(e) => setDividendPaymentDelay(parseInt(e.target.value) || 6)}
                        min="1"
                        max="9"
                        className="small-input"
                      />
                      <span className="unit">mois après clôture</span>
                    </div>
                    {/* SARL Warning: SSI on dividends > 10% capital */}
                    {(entityData.legalForm === 'SARL' || entityData.legalForm === 'EURL') &&
                     managersWithStatus.some(m => m.status === 'TNS') && (
                      <div className="sarl-dividend-warning">
                        <span className="warning-icon">⚠️</span>
                        <span className="warning-text">
                          <strong>SARL gérant TNS :</strong> Dividendes &gt; 10% du capital ({formatCurrency((parseFloat(entityData.shareCapital) || 10000) * 0.10)})
                          soumis aux cotisations SSI (~45%)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2: Exonérations + CFE + TVA */}
                <div className="hyp-row">
                  <div className="edit-section">
                    <h4>Exonérations</h4>
                    <div className="options-row">
                      <label className="option-toggle">
                        <input type="checkbox" checked={hasACRE} onChange={(e) => setHasACRE(e.target.checked)} />
                        ACRE
                      </label>
                      <label className="option-toggle">
                        <input type="checkbox" checked={advancedOptions.jei} onChange={(e) => setAdvancedOptions(prev => ({ ...prev, jei: e.target.checked }))} />
                        JEI
                      </label>
                      <label className="option-toggle">
                        <input type="checkbox" checked={hasCGA} onChange={(e) => setHasCGA(e.target.checked)} />
                        CGA
                      </label>
                    </div>
                  </div>

                  <div className="edit-section">
                    <h4>CFE / CVAE</h4>
                    <div className="fiscal-row">
                      <label className="option-toggle">
                        <input
                          type="checkbox"
                          checked={fiscalParams.cfeHasLocal}
                          onChange={(e) => setFiscalParams(prev => ({ ...prev, cfeHasLocal: e.target.checked }))}
                        />
                        Local professionnel
                      </label>
                      {fiscalParams.cfeHasLocal && (
                        <>
                          <input
                            type="number"
                            value={fiscalParams.cfeSurface}
                            onChange={(e) => setFiscalParams(prev => ({ ...prev, cfeSurface: parseFloat(e.target.value) || 0 }))}
                            className="small-input"
                          />
                          <span className="unit">m²</span>
                          <span className="separator">×</span>
                          <input
                            type="number"
                            value={fiscalParams.cfeTarifM2}
                            onChange={(e) => setFiscalParams(prev => ({ ...prev, cfeTarifM2: parseFloat(e.target.value) || 0 }))}
                            className="small-input"
                          />
                          <span className="unit">€/m²</span>
                        </>
                      )}
                      <span className="separator">|</span>
                      <label>Taux</label>
                      <input
                        type="number"
                        value={fiscalParams.cfeTaux}
                        onChange={(e) => setFiscalParams(prev => ({ ...prev, cfeTaux: parseFloat(e.target.value) || 0 }))}
                        className="small-input"
                      />
                      <span className="unit">%</span>
                    </div>
                  </div>

                  <div className="edit-section">
                    <h4>TVA</h4>
                    <div className="tva-row">
                      <select
                        value={tvaRegime}
                        onChange={(e) => setTvaRegime(e.target.value)}
                        className="tva-select"
                      >
                        <option value="non_assujetti">Franchise</option>
                        <option value="reel_simplifie">Simplifié</option>
                        <option value="reel_normal">Normal</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Header avec dates */}
          <div className={`ca-dates-header ${!hasEndDate ? 'needs-end-date' : ''}`}>
            <div className="date-field">
              <label>Début</label>
              <input
                type="date"
                value={forecastDates.startDate}
                onChange={(e) => setForecastDates(prev => ({ ...prev, startDate: e.target.value }))}
                className="source-date"
              />
            </div>
            <span className="date-sep">→</span>
            <div className={`date-field ${!hasEndDate ? 'highlight' : ''}`}>
              <label>Fin {!hasEndDate && <span className="required">*</span>}</label>
              <input
                type="date"
                value={forecastDates.endDate}
                onChange={(e) => setForecastDates(prev => ({ ...prev, endDate: e.target.value }))}
                className={`source-date ${!hasEndDate ? 'pulse' : ''}`}
              />
            </div>
            {hasEndDate && forecastExercices.length > 0 && (
              <div className="mode-selector">
                <span className="mode-selector-label">Mode :</span>
                <div className="mode-selector-cards">
                  <Tooltip text="Vue résumée avec totaux agrégés par exercice. Idéal pour une vision rapide.">
                    <button
                      type="button"
                      className={`mode-card ${!isDetailedMode ? 'selected' : ''}`}
                      onClick={() => { setStep3Mode('simplified'); setChargeMode('simplified'); }}
                    >
                      <span className="mode-card-icon">📊</span>
                      <span className="mode-card-label">Simplifié</span>
                    </button>
                  </Tooltip>
                  <Tooltip text="Vue complète avec le détail de chaque catégorie. Pour un contrôle fin des données.">
                    <button
                      type="button"
                      className={`mode-card ${isDetailedMode ? 'selected' : ''}`}
                      onClick={() => { setStep3Mode('detailed'); setChargeMode('detailed'); }}
                    >
                      <span className="mode-card-icon">📋</span>
                      <span className="mode-card-label">Détaillé</span>
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>

          {/* Message si pas de date de fin */}
          {!hasEndDate ? (
            <div className="empty-sources waiting">
              <span className="arrow-up">↑</span>
              <p>Saisissez la date de fin pour générer les exercices</p>
              <span className="empty-hint">Clôture fiscale : {fiscalYearEnd || '31/12'}</span>
            </div>
          ) : forecastExercices.length === 0 ? (
            <div className="empty-sources">
              <p>Aucun exercice dans cette période</p>
            </div>
          ) : (
            /* Liste des exercices - format compact */
            <div className="exercices-compact-list">
              {(() => {
                // Pre-calculate affectations with cascading report and cumulative reserves
                const affectationsMap = {};
                let runningReport = 0;
                let runningReserves = 0;
                forecastExercices.forEach((ex) => {
                  const exTotals = calculateExerciceTotals(ex);
                  const aff = getAffectationForExercice(ex, exTotals.resultatNet, runningReport, runningReserves);
                  affectationsMap[ex.key] = aff;
                  runningReport = aff.reportNouveau;
                  runningReserves = aff.newCumulReserves;
                });
                return forecastExercices.map((exercice, index) => {
                const data = getExerciceDataForYear(exercice.key);
                const totals = calculateExerciceTotals(exercice);
                const affectation = affectationsMap[exercice.key];

                // Check if exercice is clipped by forecast dates
                const isFirstExercice = index === 0;
                const isLastExercice = index === forecastExercices.length - 1;
                const parseDate = (str) => {
                  if (!str) return null;
                  const p = str.split('/');
                  if (p.length === 3) return new Date(parseInt(p[2]) + (parseInt(p[2]) < 50 ? 2000 : 1900), parseInt(p[1]) - 1, parseInt(p[0]));
                  return null;
                };
                const formatShort = (d) => d ? `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getFullYear()).slice(-2)}` : '';
                // Normalize date to midnight local to avoid timezone comparison issues
                const normalizeDate = (d) => {
                  if (!d) return null;
                  const date = new Date(d);
                  date.setHours(0, 0, 0, 0);
                  return date;
                };

                const exStart = parseDate(exercice.startDate);
                const exEnd = parseDate(exercice.endDate);
                const fcStart = normalizeDate(forecastDates.startDate);
                const fcEnd = normalizeDate(forecastDates.endDate);

                const isStartClipped = isFirstExercice && fcStart && exStart && fcStart > exStart;
                const isEndClipped = isLastExercice && fcEnd && exEnd && fcEnd < exEnd;
                const isClipped = isStartClipped || isEndClipped;

                // Dates EFFECTIVES (après clipping par la période prévisionnelle)
                const effectiveStart = isStartClipped ? fcStart : exStart;
                const effectiveEnd = isEndClipped ? fcEnd : exEnd;
                const effectiveStartStr = formatShort(effectiveStart);
                const effectiveEndStr = formatShort(effectiveEnd);

                return (
                  <div key={exercice.key} className="exercice-card">
                    {/* Header exercice compact */}
                    <div className="exercice-header">
                      <strong>{exercice.label}</strong>
                      <span className={`periode ${isClipped ? 'clipped' : ''}`}>
                        {effectiveStartStr} → {effectiveEndStr}
                        {isClipped && (
                          <Tooltip text={`Période ajustée aux dates prévisionnelles. L'exercice fiscal complet est ${exercice.startDate} → ${exercice.endDate}, mais seule la partie comprise dans la période prévisionnelle est comptabilisée.`}>
                            <span className="clip-icon">✂ ajusté</span>
                          </Tooltip>
                        )}
                      </span>
                      {exercice.isFirstYear && <span className="tag y1">An 1</span>}
                      {exercice.isSecondYear && <span className="tag y2">An 2</span>}
                    </div>

                    {/* Tableau compact */}
                    <div className="exercice-body">
                      {/* PRODUITS (Classe 7) */}
                      <div className="section-row produits">
                        <div className="section-label">
                          <span className="icon">+</span>
                          <span className="pcg-num">7</span>
                          Produits
                        </div>
                        <div className="section-value">{formatCurrency(totals.totalProduits)}</div>
                      </div>

                      {/* CA - éditable (70) */}
                      <div className="line-row ca">
                        <div className="line-label">
                          <span className="pcg-num">70</span>
                          {isDetailedMode ? (
                            <input
                              type="text"
                              value={data.caPrincipal?.label || ''}
                              onChange={(e) => updateCAPrincipal(exercice.key, { label: e.target.value })}
                              placeholder="CA Principal"
                              className="label-input"
                            />
                          ) : (
                            <span>Chiffre d'affaires</span>
                          )}
                        </div>
                        <div className="line-input">
                          <input
                            type="number"
                            value={data.caPrincipal?.amount || ''}
                            onChange={(e) => updateCAPrincipal(exercice.key, { amount: parseFloat(e.target.value) || 0 })}
                            placeholder="0"
                          />
                          <span>€</span>
                        </div>
                      </div>

                      {/* CA Secondaires - éditable en mode détaillé */}
                      {isDetailedMode && (data.caSecondaires || []).map((source, idx) => (
                        <div key={source.id} className="line-row ca secondary">
                          <div className="line-label">
                            <span className="pcg-num">70</span>
                            <input
                              type="text"
                              value={source.label || ''}
                              onChange={(e) => updateCASecondaire(exercice.key, source.id, { label: e.target.value })}
                              placeholder={`CA Secondaire ${idx + 1}`}
                              className="label-input"
                            />
                            <button
                              type="button"
                              className="mini-btn remove"
                              onClick={() => removeCASecondaire(exercice.key, source.id)}
                              title="Supprimer"
                            >×</button>
                          </div>
                          <div className="line-input">
                            <input
                              type="number"
                              value={source.amount || ''}
                              onChange={(e) => updateCASecondaire(exercice.key, source.id, { amount: parseFloat(e.target.value) || 0 })}
                              placeholder="0"
                            />
                            <span>€</span>
                          </div>
                        </div>
                      ))}

                      {/* Boutons ajout produits - mode détaillé uniquement */}
                      {isDetailedMode && (
                        <div className="add-row products-add">
                          <button type="button" onClick={() => addCASecondaire(exercice.key)}>+ CA</button>
                          <button type="button" onClick={() => addSubvention(exercice.key)}>+ Subvention</button>
                          <button type="button" onClick={() => addAutreProduit(exercice.key)}>+ Autre</button>
                        </div>
                      )}

                      {/* Subventions - éditable en mode détaillé */}
                      {isDetailedMode && (data.subventions || []).map((sub, idx) => (
                        <div key={sub.id} className="line-row ca secondary">
                          <div className="line-label">
                            <span className="pcg-num">74</span>
                            <input
                              type="text"
                              value={sub.label || ''}
                              onChange={(e) => updateSubvention(exercice.key, sub.id, { label: e.target.value })}
                              placeholder={`Subvention ${idx + 1}`}
                              className="label-input"
                            />
                            <button
                              type="button"
                              className="mini-btn remove"
                              onClick={() => removeSubvention(exercice.key, sub.id)}
                              title="Supprimer"
                            >×</button>
                          </div>
                          <div className="line-input">
                            <input
                              type="number"
                              value={sub.amount || ''}
                              onChange={(e) => updateSubvention(exercice.key, sub.id, { amount: parseFloat(e.target.value) || 0 })}
                              placeholder="0"
                            />
                            <span>€</span>
                          </div>
                        </div>
                      ))}

                      {/* Autres produits - éditable en mode détaillé */}
                      {isDetailedMode && (data.autresProduits || []).map((prod, idx) => (
                        <div key={prod.id} className="line-row ca secondary">
                          <div className="line-label">
                            <span className="pcg-num">75</span>
                            <input
                              type="text"
                              value={prod.label || ''}
                              onChange={(e) => updateAutreProduit(exercice.key, prod.id, { label: e.target.value })}
                              placeholder={`Autre produit ${idx + 1}`}
                              className="label-input"
                            />
                            <button
                              type="button"
                              className="mini-btn remove"
                              onClick={() => removeAutreProduit(exercice.key, prod.id)}
                              title="Supprimer"
                            >×</button>
                          </div>
                          <div className="line-input">
                            <input
                              type="number"
                              value={prod.amount || ''}
                              onChange={(e) => updateAutreProduit(exercice.key, prod.id, { amount: parseFloat(e.target.value) || 0 })}
                              placeholder="0"
                            />
                            <span>€</span>
                          </div>
                        </div>
                      ))}

                      {/* CHARGES (Classe 6) */}
                      <div className={`section-row charges ${!isDetailedMode ? 'clickable' : ''}`}
                        onClick={!isDetailedMode ? () => setActiveModal({ type: 'chargeSummary', exerciceKey: exercice.key, exercice }) : undefined}
                      >
                        <div className="section-label">
                          <span className="icon">−</span>
                          <span className="pcg-num">6</span>
                          Charges
                          {!isDetailedMode && <span className="expand-hint">cliquer pour détail</span>}
                        </div>
                        <div className="section-value neg">-{formatCurrency(totals.chargesGlobales)}</div>
                      </div>

                      {/* Détail charges selon PCG - seulement en mode détaillé */}
                      {isDetailedMode && (
                        <>
                          {/* 60 - Achats (charges variables liées au CA) */}
                          <div className="line-row charge-line">
                            <div className="line-label">
                              <span className="pcg-num">60</span>
                              Achats
                            </div>
                            <div className="line-value-with-btn">
                              <span className="neg">-{formatCurrency(totals.totalChargesVar)}</span>
                              <button
                                type="button"
                                className="mini-btn info"
                                onClick={() => setActiveModal({ type: 'chargeAchats', exerciceKey: exercice.key, exercice })}
                                title="Configurer les charges variables par activité"
                              >⚙</button>
                            </div>
                          </div>

                          {/* 61-62 - Services extérieurs */}
                          <div className="line-row charge-line">
                            <div className="line-label">
                              <span className="pcg-num">61-62</span>
                              Services ext.
                            </div>
                            <div className="line-value-with-btn">
                              <span className="neg">-{formatCurrency(totals.chargesExploitation)}</span>
                              <button
                                type="button"
                                className="mini-btn info"
                                onClick={() => setActiveModal({ type: 'chargeExploitation', exerciceKey: exercice.key, exercice })}
                                title="Voir le détail des services extérieurs"
                              >ⓘ</button>
                            </div>
                          </div>

                          {/* 63 - Impôts, taxes */}
                          <div className="line-row charge-line">
                            <div className="line-label">
                              <span className="pcg-num">63</span>
                              Impôts, taxes
                              {exercice.isFirstYear && <span className="mini-tag exo">CFE exo</span>}
                              {exercice.isSecondYear && <span className="mini-tag reduc">CFE -50%</span>}
                              {totals.cvae > 0 && <span className="mini-tag cvae">+CVAE</span>}
                            </div>
                            <div className="line-value-with-btn">
                              <span className="neg">-{formatCurrency(totals.totalImpotsEtTaxes)}</span>
                              <button
                                type="button"
                                className="mini-btn info"
                                onClick={() => setActiveModal({ type: 'chargeImpots', exerciceKey: exercice.key, exercice })}
                                title="Voir le détail CFE, CVAE, taxes"
                              >ⓘ</button>
                            </div>
                          </div>

                          {/* 64 - Charges de personnel */}
                          <div
                            className="line-row charge-line clickable-line"
                            onClick={() => setActiveModal({ type: 'chargePersonnel', exerciceKey: exercice.key, exercice })}
                            title="Cliquez pour configurer les dirigeants et employés"
                          >
                            <div className="line-label">
                              <span className="pcg-num">64</span>
                              Personnel
                              {(() => {
                                const yearManagers = getEffectiveManagers(exercice.key);
                                const linkedManagers = yearManagers.filter(m => m.individualId);
                                const unlinkedManagers = yearManagers.filter(m => !m.individualId);
                                if (linkedManagers.length > 0 || unlinkedManagers.length > 0) {
                                  return (
                                    <span className="linked-indicator">
                                      {linkedManagers.length > 0 && (
                                        <span className="linked-badge" title={linkedManagers.map(m => getRoleLabel(m.role)).join(', ')}>
                                          👤 {linkedManagers.length} lié{linkedManagers.length > 1 ? 's' : ''}
                                        </span>
                                      )}
                                      {unlinkedManagers.length > 0 && (
                                        <span className="unlinked-badge" title={unlinkedManagers.map(m => getRoleLabel(m.role)).join(', ')}>
                                          ⚠ {unlinkedManagers.length} non lié{unlinkedManagers.length > 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="line-value-with-btn">
                              <span className="neg">-{formatCurrency(totals.coutDirigeants + totals.coutEmployes)}</span>
                              <button
                                type="button"
                                className="mini-btn info"
                                onClick={(e) => { e.stopPropagation(); setActiveModal({ type: 'chargePersonnel', exerciceKey: exercice.key, exercice }); }}
                                title="Voir le détail dirigeants et employés"
                              >ⓘ</button>
                            </div>
                          </div>
                        </>
                      )}

                      {/* RÉSULTAT AVANT IS */}
                      <div className={`section-row resultat-avant-is ${totals.resultatAvantIS < 0 ? 'neg' : 'pos'}`}>
                        <div className="section-label">
                          <span className="icon">=</span>
                          Résultat avant IS
                        </div>
                        <div className="section-value">{formatCurrency(totals.resultatAvantIS)}</div>
                      </div>

                      {/* IS - Impôt sur les Sociétés */}
                      {totals.isAmount > 0 && (
                        <div className="section-row is-row">
                          <div className="section-label">
                            <span className="icon">−</span>
                            IS <span className="is-rate">(15%/25%)</span>
                          </div>
                          <div className="section-value neg">-{formatCurrency(totals.isAmount)}</div>
                        </div>
                      )}

                      {/* RÉSULTAT NET */}
                      <div className={`section-row resultat ${totals.resultatNet < 0 ? 'neg' : 'pos'}`}>
                        <div className="section-label">
                          <span className="icon">=</span>
                          <strong>Résultat Net</strong>
                        </div>
                        <div className="section-value"><strong>{formatCurrency(totals.resultatNet)}</strong></div>
                      </div>

                      {/* AFFECTATION DU RÉSULTAT */}
                      {totals.resultatNet !== 0 && (
                        <>
                          {/* Séparateur pointillé */}
                          <div className="affectation-separator"></div>

                          {/* Report N-1 si existant */}
                          {affectation.reportNMoins1 !== 0 && (
                            <div className="line-row report-n1-row">
                              <div className="line-label">
                                <span className="pcg-num">{affectation.reportNMoins1 < 0 ? '119' : '110'}</span>
                                Report N-1
                              </div>
                              <div className="line-value">
                                <span className={`amount ${affectation.reportNMoins1 < 0 ? 'neg' : 'pos'}`}>
                                  {formatCurrency(affectation.reportNMoins1)}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* AFFECTATION - all config on one line */}
                          <div className={`section-row affectation ${affectation.isOverridden ? 'overridden' : ''}`}>
                            <div className="section-label">
                              <span className="icon">📋</span>
                              <strong>Affectation</strong>
                            </div>
                            <div className="section-value affectation-inputs">
                              <span className="input-group">
                                <span className="input-label">AG</span>
                                <input
                                  type="date"
                                  value={affectation.agDate ? affectation.agDate.toISOString().split('T')[0] : ''}
                                  onChange={(e) => updateAGDate(exercice.key, e.target.value)}
                                  className="date-input"
                                />
                              </span>
                              <span className="input-group">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={affectation.percent}
                                  onChange={(e) => updateDividendPercent(exercice.key, parseFloat(e.target.value) || 0)}
                                  className="pct-input"
                                />
                                <span className="input-label">%</span>
                              </span>
                              <span className="input-group">
                                <span className="input-label">Versé</span>
                                <input
                                  type="date"
                                  value={affectation.paymentDate ? affectation.paymentDate.toISOString().split('T')[0] : ''}
                                  onChange={(e) => updateDividendPaymentDate(exercice.key, e.target.value)}
                                  className="date-input"
                                />
                              </span>
                              <button
                                type="button"
                                className="mini-btn info"
                                onClick={() => setActiveModal({
                                  type: 'affectationDetail',
                                  exercice,
                                  affectation
                                })}
                                title="Voir le détail"
                              >ⓘ</button>
                            </div>
                          </div>

                          {/* Ligne Réserve légale */}
                          {affectation.reserveLegale > 0 && (
                            <div className="line-row reserve-legale-row">
                              <div className="line-label">
                                <span className="pcg-num">106</span>
                                Réserve légale (5%)
                                {affectation.reserveRestante === 0 && (
                                  <span className="plafond-badge">Plafond atteint</span>
                                )}
                              </div>
                              <div className="line-value">
                                <span className="amount neg">-{formatCurrency(affectation.reserveLegale)}</span>
                              </div>
                            </div>
                          )}

                          {/* Ligne Dividendes */}
                          <div className="line-row dividendes-row">
                            <div className="line-label">
                              <span className="pcg-num">457</span>
                              Dividendes
                              {affectation.taxYear && <span className="tax-year-badge">IR {affectation.taxYear}</span>}
                            </div>
                            <div className="line-value">
                              <span className={`amount ${affectation.dividendes > 0 ? 'pos' : ''}`}>{formatCurrency(affectation.dividendes)}</span>
                            </div>
                          </div>

                          {/* SARL Warning: dividends > 10% capital */}
                          {affectation.sarlWarning && (
                            <div className="sarl-dividend-warning">
                              <span className="warning-icon">⚠️</span>
                              <span className="warning-text">
                                SARL : {formatCurrency(affectation.sarlWarning.excedent)} de dividendes dépassent le seuil de 10% du capital ({formatCurrency(affectation.sarlWarning.seuil)}).
                                <br />L'excédent sera soumis aux cotisations SSI (~{formatCurrency(affectation.sarlWarning.chargesSSI)}).
                              </span>
                            </div>
                          )}

                          {/* Ligne Report à nouveau */}
                          <div className="line-row report-row">
                            <div className="line-label">
                              <span className="pcg-num">{affectation.reportNouveau < 0 ? '119' : '110'}</span>
                              Report à nouveau
                              {index < forecastExercices.length - 1 && <span className="report-hint">→ N+1</span>}
                            </div>
                            <div className="line-value">
                              <span className={`amount ${affectation.reportNouveau < 0 ? 'neg' : ''}`}>{formatCurrency(affectation.reportNouveau)}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              });
              })()}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* All modals rendered via portal to escape overflow:hidden */}
      {activeModal && ReactDOM.createPortal(
        <div className="entity-edit-modal-portal">
          {/* Modal for charge summary */}
          {activeModal?.type === 'chargeSummary' && (
            <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
              <div className="charges-var-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h4>Détail des charges - {activeModal.exercice.label}</h4>
                  <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
                </div>
                <div className="modal-body">
                  <table className="charges-summary-table">
                    <tbody>
                      <tr>
                        <td>60 - Achats</td>
                        <td className="neg">{formatCurrency(calculateExerciceTotals(activeModal.exercice).totalChargesVar)}</td>
                      </tr>
                      <tr>
                        <td>61-62 - Services extérieurs</td>
                        <td className="neg">{formatCurrency(calculateExerciceTotals(activeModal.exercice).chargesExploitation)}</td>
                      </tr>
                      <tr>
                        <td>63 - Impôts et taxes</td>
                        <td className="neg">{formatCurrency(calculateExerciceTotals(activeModal.exercice).totalImpotsEtTaxes)}</td>
                      </tr>
                      <tr
                        className="clickable-row"
                        onClick={() => setActiveModal({ type: 'chargePersonnel', exerciceKey: activeModal.exerciceKey, exercice: activeModal.exercice })}
                        title="Cliquez pour configurer les dirigeants"
                      >
                        <td>
                          64 - Charges de personnel
                          {(() => {
                            const yearManagers = getEffectiveManagers(activeModal.exerciceKey);
                            const linkedCount = yearManagers.filter(m => m.individualId).length;
                            const unlinkedCount = yearManagers.filter(m => !m.individualId).length;
                            return (
                              <span className="linked-indicator-inline">
                                {linkedCount > 0 && <span className="linked-badge-sm">👤 {linkedCount}</span>}
                                {unlinkedCount > 0 && <span className="unlinked-badge-sm">⚠ {unlinkedCount}</span>}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="neg">{formatCurrency(calculateExerciceTotals(activeModal.exercice).coutDirigeants + calculateExerciceTotals(activeModal.exercice).coutEmployes)}</td>
                      </tr>
                      <tr className="total-row">
                        <td><strong>Total charges</strong></td>
                        <td className="neg"><strong>{formatCurrency(calculateExerciceTotals(activeModal.exercice).chargesGlobales)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

      {/* Modal 60 - Achats et charges variables */}
      {activeModal?.type === 'chargeAchats' && (
        <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
          <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h4><span className="pcg-badge">60</span> Achats et charges variables</h4>
              <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-hint">
                Configurez les charges liées à chaque source de CA - {activeModal.exercice?.label}
              </p>

              {(() => {
                const data = getExerciceDataForYear(activeModal.exerciceKey);
                const cvPrincipal = data.caPrincipal?.chargesVariables || { type: 'percent', value: 0 };
                const montantPrincipal = cvPrincipal.type === 'percent'
                  ? Math.round((data.caPrincipal?.amount || 0) * (cvPrincipal.value || 0) / 100)
                  : (cvPrincipal.value || 0);

                let totalCharges = montantPrincipal;
                (data.caSecondaires || []).forEach(source => {
                  const cv = source.chargesVariables || { type: 'fixed', value: 0 };
                  totalCharges += cv.type === 'percent'
                    ? Math.round((source.amount || 0) * (cv.value || 0) / 100)
                    : (cv.value || 0);
                });

                return (
                  <>
                    {/* CA Principal */}
                    <div className="charge-detail-section">
                      <h5>CA Principal : {data.caPrincipal?.label || 'Activité principale'}</h5>
                      <div className="charges-config-row">
                        <span className="config-label">CA : {formatCurrency(data.caPrincipal?.amount || 0)}</span>
                        <div className="config-inputs">
                          <select
                            value={cvPrincipal.type}
                            onChange={(e) => updateCAPrincipal(activeModal.exerciceKey, {
                              chargesVariables: { ...cvPrincipal, type: e.target.value }
                            })}
                          >
                            <option value="percent">% du CA</option>
                            <option value="fixed">Montant fixe</option>
                          </select>
                          <input
                            type="number"
                            value={cvPrincipal.value || ''}
                            onChange={(e) => updateCAPrincipal(activeModal.exerciceKey, {
                              chargesVariables: { ...cvPrincipal, value: parseFloat(e.target.value) || 0 }
                            })}
                            placeholder="0"
                            min="0"
                            step={cvPrincipal.type === 'percent' ? '1' : '100'}
                          />
                          <span className="suffix">{cvPrincipal.type === 'percent' ? '%' : '€'}</span>
                        </div>
                        <span className="config-result">= {formatCurrency(montantPrincipal)}</span>
                      </div>
                    </div>

                    {/* CA Secondaires */}
                    {(data.caSecondaires || []).length > 0 && (
                      <div className="charge-detail-section">
                        <h5>Activités secondaires</h5>
                        {data.caSecondaires.map(source => {
                          const cv = source.chargesVariables || { type: 'fixed', value: 0 };
                          const montant = cv.type === 'percent'
                            ? Math.round((source.amount || 0) * (cv.value || 0) / 100)
                            : (cv.value || 0);
                          return (
                            <div key={source.id} className="charges-config-row">
                              <span className="config-label">
                                {source.label || 'Secondaire'} : {formatCurrency(source.amount || 0)}
                              </span>
                              <div className="config-inputs">
                                <select
                                  value={cv.type}
                                  onChange={(e) => updateCASecondaire(activeModal.exerciceKey, source.id, {
                                    chargesVariables: { ...cv, type: e.target.value }
                                  })}
                                >
                                  <option value="percent">% du CA</option>
                                  <option value="fixed">Montant fixe</option>
                                </select>
                                <input
                                  type="number"
                                  value={cv.value || ''}
                                  onChange={(e) => updateCASecondaire(activeModal.exerciceKey, source.id, {
                                    chargesVariables: { ...cv, value: parseFloat(e.target.value) || 0 }
                                  })}
                                  placeholder="0"
                                  min="0"
                                  step={cv.type === 'percent' ? '1' : '100'}
                                />
                                <span className="suffix">{cv.type === 'percent' ? '%' : '€'}</span>
                              </div>
                              <span className="config-result">= {formatCurrency(montant)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {(data.caSecondaires || []).length === 0 && (
                      <p className="info-text">
                        Ajoutez des activités secondaires pour leur affecter des charges spécifiques.
                      </p>
                    )}

                    {/* Total */}
                    <div className="charge-total-row">
                      <span>Total compte 60 :</span>
                      <strong className="neg">-{formatCurrency(totalCharges)}</strong>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="modal-footer">
              <span className="footer-hint">Ex: achats marchandises, sous-traitance, commissions</span>
              <div className="footer-buttons">
                <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal 61-62 - Services extérieurs */}
      {activeModal?.type === 'chargeExploitation' && (() => {
        // Get per-year data (falls back to global)
        const yearKey = activeModal.exerciceKey;
        const yearData = getExerciceDataForYear(yearKey);
        const yearSimplified = yearData.simplifiedCharges ?? simplifiedCharges;
        const yearDetailed = yearData.detailedCharges ?? detailedCharges;
        const yearMode = yearData.chargeMode ?? chargeMode;
        const effectiveMode = step3Mode ?? yearMode;

        // Update per-year simplified charges
        const updateYearSimplified = (updates) => {
          setExerciceData(prev => ({
            ...prev,
            [yearKey]: {
              ...getExerciceDataForYear(yearKey),
              simplifiedCharges: { ...yearSimplified, ...updates }
            }
          }));
        };

        // Update per-year detailed charges
        const updateYearDetailed = (lineKey, updates) => {
          setExerciceData(prev => ({
            ...prev,
            [yearKey]: {
              ...getExerciceDataForYear(yearKey),
              detailedCharges: { ...yearDetailed, [lineKey]: { ...(yearDetailed[lineKey] || {}), ...updates } }
            }
          }));
        };

        return (
        <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
          <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h4><span className="pcg-badge">61-62</span> Services extérieurs - {activeModal.exercice?.label}</h4>
              <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Toggle mode */}
              <div className="mode-toggle-row">
                <span className="mode-label">Mode :</span>
                <div className="mode-toggle">
                  <button className={effectiveMode === 'simplified' ? 'active' : ''} onClick={() => setStep3Mode('simplified')}>Simplifié</button>
                  <button className={effectiveMode === 'detailed' ? 'active' : ''} onClick={() => setStep3Mode('detailed')}>Détaillé</button>
                </div>
              </div>

              {effectiveMode === 'simplified' ? (
                /* Mode simplifié : ligne unique */
                <div className="simple-grid">
                  <div className="grid-row header">
                    <div className="col-type">Type</div>
                    <div className="col-value">Valeur</div>
                  </div>
                  <div className={`grid-row ${!yearSimplified.usePercent ? 'selected' : ''}`} onClick={() => updateYearSimplified({ usePercent: false })}>
                    <div className="col-type">
                      <input type="radio" checked={!yearSimplified.usePercent} readOnly />
                      <span>Montant fixe annuel</span>
                    </div>
                    <div className="col-value">
                      <input
                        type="number"
                        value={yearSimplified.totalFixed || ''}
                        onChange={(e) => updateYearSimplified({ totalFixed: parseFloat(e.target.value) || 0 })}
                        placeholder="25000"
                        disabled={yearSimplified.usePercent}
                      />
                      <span className="unit">€/an</span>
                    </div>
                  </div>
                  <div className={`grid-row ${yearSimplified.usePercent ? 'selected' : ''}`} onClick={() => updateYearSimplified({ usePercent: true })}>
                    <div className="col-type">
                      <input type="radio" checked={yearSimplified.usePercent} readOnly />
                      <span>Pourcentage du CA</span>
                    </div>
                    <div className="col-value">
                      <input
                        type="number"
                        value={yearSimplified.totalPercent || ''}
                        onChange={(e) => updateYearSimplified({ totalPercent: parseFloat(e.target.value) || 0 })}
                        placeholder="20"
                        disabled={!yearSimplified.usePercent}
                      />
                      <span className="unit">%</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Mode détaillé : tableau */
                <div className="charges-table">
                  <div className="table-header">
                    <div className="col-check"></div>
                    <div className="col-label">Poste de charge</div>
                    <div className="col-type">Type</div>
                    <div className="col-amount">Montant</div>
                  </div>
                  <div className="table-body">
                    {COMMON_CHARGE_LINES.filter(line =>
                      !['salaires_employes', 'charges_sociales_employes'].includes(line.key)
                    ).map(line => {
                      const chargeData = yearDetailed[line.key] || { enabled: false, value: line.defaultValue || 0, type: 'fixed' };
                      return (
                        <div key={line.key} className={`table-row ${chargeData.enabled ? 'enabled' : ''}`}>
                          <div className="col-check">
                            <input
                              type="checkbox"
                              checked={chargeData.enabled}
                              onChange={(e) => updateYearDetailed(line.key, { enabled: e.target.checked, value: chargeData.value || line.defaultValue || 0, type: chargeData.type || 'fixed' })}
                            />
                          </div>
                          <div className="col-label">
                            <span className="icon">{line.icon}</span>
                            <span>{line.label}</span>
                          </div>
                          <div className="col-type">
                            <select
                              value={chargeData.type}
                              onChange={(e) => updateYearDetailed(line.key, { ...chargeData, type: e.target.value })}
                              disabled={!chargeData.enabled}
                            >
                              <option value="fixed">€/an</option>
                              <option value="percent">% CA</option>
                            </select>
                          </div>
                          <div className="col-amount">
                            <input
                              type="number"
                              value={chargeData.enabled ? (chargeData.value || '') : ''}
                              onChange={(e) => updateYearDetailed(line.key, { ...chargeData, value: parseFloat(e.target.value) || 0 })}
                              placeholder={String(line.defaultValue || 0)}
                              disabled={!chargeData.enabled}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="modal-total">
                <span>Total compte 61-62 ({activeModal.exercice?.label})</span>
                <strong>-{formatCurrency(calculateExerciceTotals(activeModal.exercice).chargesExploitation)}</strong>
              </div>
            </div>
            <div className="modal-footer">
              <span className="footer-hint">Valeurs pour {activeModal.exercice?.label}</span>
              <div className="footer-buttons">
                <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Modal 63 Impôts et taxes - FULL VERSION from WizardAddEntity */}
      {activeModal?.type === 'chargeImpots' && (
        <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
          <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h4><span className="pcg-badge">63</span> Impôts, taxes et versements</h4>
              <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-hint">
                Hypothèses modifiables → calculs automatiques selon les règles fiscales
              </p>

              {/* Tableau grille des impôts */}
              <div className="charges-table impots-table">
                <div className="table-header">
                  <div className="col-impot-label">Impôt / Taxe</div>
                  <div className="col-impot-hypothese">Hypothèses <Tooltip text="Valeurs de base modifiables servant au calcul automatique"><span className="help-icon">ⓘ</span></Tooltip></div>
                  <div className="col-impot-calcul">Formule</div>
                  <div className="col-impot-montant">Montant</div>
                </div>
                <div className="table-body">
                  {/* CFE - Cotisation Foncière des Entreprises */}
                  {(() => {
                    const totals = calculateExerciceTotals(activeModal.exercice);
                    const ca = totals.totalCA || 0;

                    const baseMinAuto = getCfeBaseMinimaleFromCA(ca);
                    const baseMinEffective = fiscalParams.cfeBaseMinimale || baseMinAuto;
                    const cotisationMin = Math.round(baseMinEffective * fiscalParams.cfeTaux / 100);

                    let cfeBase = 0;
                    let cfeCalculee = 0;
                    let useMin = false;

                    if (fiscalParams.cfeHasLocal) {
                      cfeBase = fiscalParams.cfeSurface * fiscalParams.cfeTarifM2;
                      const cfeFromLocal = Math.round(cfeBase * fiscalParams.cfeTaux / 100);
                      if (cfeFromLocal < cotisationMin) {
                        cfeCalculee = cotisationMin;
                        useMin = true;
                      } else {
                        cfeCalculee = cfeFromLocal;
                      }
                    } else {
                      cfeBase = baseMinEffective;
                      cfeCalculee = cotisationMin;
                    }

                    const cfeAvantExo = cfeCalculee;
                    let cfeFinal = cfeAvantExo;
                    let exoLabel = '';
                    if (activeModal.exercice?.isFirstYear) {
                      cfeFinal = 0;
                      exoLabel = 'Exo 100% (an 1)';
                    } else if (activeModal.exercice?.isSecondYear) {
                      cfeFinal = Math.round(cfeAvantExo * 0.5);
                      exoLabel = '-50% (an 2)';
                    }

                    return (
                      <div className="table-row impot-row cfe-row">
                        <div className="col-impot-label">
                          <strong>CFE</strong>
                          <span className="impot-subtitle">Cotisation Foncière des Entreprises</span>
                          <span className="impot-ca-info" style={{display: 'block', fontSize: '10px', color: '#666', marginTop: '2px'}}>
                            CA: {formatCurrency(ca)} → Min CFE: {formatCurrency(cotisationMin)}
                          </span>
                        </div>
                        <div className="col-impot-hypothese">
                          <div className="hypothesis-inputs">
                            <div className="input-row toggle-row">
                              <div className="mode-toggle compact">
                                <button
                                  type="button"
                                  className={fiscalParams.cfeHasLocal ? 'active' : ''}
                                  onClick={() => {
                                    setFiscalParams(p => ({ ...p, cfeHasLocal: true }));
                                    setExerciceOverrides(prev => ({
                                      ...prev,
                                      [activeModal.exercice?.key]: {
                                        ...prev[activeModal.exercice?.key],
                                        cfe: null
                                      }
                                    }));
                                  }}
                                >
                                  Avec local
                                </button>
                                <button
                                  type="button"
                                  className={!fiscalParams.cfeHasLocal ? 'active' : ''}
                                  onClick={() => {
                                    setFiscalParams(p => ({ ...p, cfeHasLocal: false }));
                                    setExerciceOverrides(prev => ({
                                      ...prev,
                                      [activeModal.exercice?.key]: {
                                        ...prev[activeModal.exercice?.key],
                                        cfe: null
                                      }
                                    }));
                                  }}
                                >
                                  Sans local
                                </button>
                              </div>
                            </div>

                            {fiscalParams.cfeHasLocal ? (
                              <>
                                <div className="input-row">
                                  <label>Surface <Tooltip text="Surface des locaux professionnels"><span className="help-icon">ⓘ</span></Tooltip></label>
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={fiscalParams.cfeSurface || ''}
                                      onChange={(e) => setFiscalParams(p => ({ ...p, cfeSurface: parseFloat(e.target.value) || 0 }))}
                                      min="0"
                                      step="5"
                                    />
                                    <span>m²</span>
                                  </div>
                                </div>
                                <div className="input-row">
                                  <label>Tarif/m² <Tooltip text="Tarif cadastral au m² (10-25€ selon zone)"><span className="help-icon">ⓘ</span></Tooltip></label>
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={fiscalParams.cfeTarifM2 || ''}
                                      onChange={(e) => setFiscalParams(p => ({ ...p, cfeTarifM2: parseFloat(e.target.value) || 0 }))}
                                      min="0"
                                      max="50"
                                      step="1"
                                    />
                                    <span>€</span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="input-row">
                                <label>Base minimale <Tooltip text={`Base fixée par la commune selon CA. Auto: ${formatCurrency(baseMinAuto)}`}><span className="help-icon">ⓘ</span></Tooltip></label>
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    value={fiscalParams.cfeBaseMinimale || ''}
                                    onChange={(e) => setFiscalParams(p => ({ ...p, cfeBaseMinimale: parseFloat(e.target.value) || null }))}
                                    placeholder={baseMinAuto}
                                    min="0"
                                    step="100"
                                  />
                                  <span>€</span>
                                </div>
                              </div>
                            )}

                            <div className="input-row">
                              <label>Taux communal <Tooltip text="Taux fixé par la commune (20-40%)"><span className="help-icon">ⓘ</span></Tooltip></label>
                              <div className="input-with-suffix">
                                <input
                                  type="number"
                                  value={fiscalParams.cfeTaux || ''}
                                  onChange={(e) => setFiscalParams(p => ({ ...p, cfeTaux: parseFloat(e.target.value) || 0 }))}
                                  min="0"
                                  max="50"
                                  step="0.5"
                                />
                                <span>%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-impot-calcul">
                          <div className="calcul-stack">
                            {fiscalParams.cfeHasLocal ? (
                              <>
                                <span className="calcul-formula">{fiscalParams.cfeSurface}m² × {fiscalParams.cfeTarifM2}€ = {formatCurrency(cfeBase)}</span>
                                <span className="calcul-formula">{formatCurrency(cfeBase)} × {fiscalParams.cfeTaux}% = {formatCurrency(Math.round(cfeBase * fiscalParams.cfeTaux / 100))}</span>
                                {useMin && (
                                  <span className="calcul-note warning">→ Min. {formatCurrency(cotisationMin)} (CA {formatCurrency(ca)})</span>
                                )}
                              </>
                            ) : (
                              <>
                                <span className="calcul-formula">Base min: {formatCurrency(cfeBase)} (CA {formatCurrency(ca)})</span>
                                <span className="calcul-formula">{formatCurrency(cfeBase)} × {fiscalParams.cfeTaux}% = {formatCurrency(cfeAvantExo)}</span>
                              </>
                            )}
                            {exoLabel && <span className="calcul-formula positive">{exoLabel}</span>}
                          </div>
                        </div>
                        <div className="col-impot-montant">
                          <div className="editable-amount">
                            <input
                              type="number"
                              value={exerciceOverrides[activeModal.exercice?.key]?.cfe ?? cfeFinal}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setExerciceOverrides(prev => ({
                                  ...prev,
                                  [activeModal.exercice?.key]: {
                                    ...prev[activeModal.exercice?.key],
                                    cfe: isNaN(val) ? null : val
                                  }
                                }));
                              }}
                              className="amount-input"
                            />
                            <span className="currency">€</span>
                            {exerciceOverrides[activeModal.exercice?.key]?.cfe != null && (
                              <button
                                type="button"
                                className="btn-reset"
                                onClick={() => setExerciceOverrides(prev => ({
                                  ...prev,
                                  [activeModal.exercice?.key]: {
                                    ...prev[activeModal.exercice?.key],
                                    cfe: null
                                  }
                                }))}
                                title="Réinitialiser au calcul auto"
                              >↺</button>
                            )}
                          </div>
                          {exerciceOverrides[activeModal.exercice?.key]?.cfe == null && (
                            <span className="calc-indicator">auto</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* CVAE - Cotisation sur la Valeur Ajoutée */}
                  {(() => {
                    const totals = calculateExerciceTotals(activeModal.exercice);
                    const ca = totals.totalProduits || 0;
                    const isApplicable = ca >= 500000;

                    let tauxCVAE = 0;
                    if (ca >= 500000 && ca < 3000000) tauxCVAE = 0.25;
                    else if (ca >= 3000000 && ca < 10000000) tauxCVAE = 0.45;
                    else if (ca >= 10000000 && ca < 50000000) tauxCVAE = 0.55;
                    else if (ca >= 50000000) tauxCVAE = 0.75;

                    const va = Math.round(ca * fiscalParams.cvaeVAPercent / 100);
                    const cvaeAmount = isApplicable ? Math.round(va * tauxCVAE / 100) : 0;

                    return (
                      <div className={`table-row impot-row ${!isApplicable ? 'disabled' : ''}`}>
                        <div className="col-impot-label">
                          <strong>CVAE</strong>
                          <span className="impot-subtitle">Cotisation sur la Valeur Ajoutée</span>
                        </div>
                        <div className="col-impot-hypothese">
                          {isApplicable ? (
                            <div className="hypothesis-inputs">
                              <div className="input-row">
                                <label>VA en % du CA <Tooltip text="Valeur ajoutée = CA - achats - services externes"><span className="help-icon">ⓘ</span></Tooltip></label>
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    value={fiscalParams.cvaeVAPercent || ''}
                                    onChange={(e) => setFiscalParams(p => ({ ...p, cvaeVAPercent: parseFloat(e.target.value) || 0 }))}
                                    min="0"
                                    max="100"
                                  />
                                  <span>%</span>
                                </div>
                              </div>
                              <span className="info-line">VA estimée: {formatCurrency(va)}</span>
                            </div>
                          ) : (
                            <span className="not-applicable">CA &lt; 500 000 € → Non due</span>
                          )}
                        </div>
                        <div className="col-impot-calcul">
                          {isApplicable ? (
                            <span className="calcul-formula">{formatCurrency(va)} × {tauxCVAE}%</span>
                          ) : (
                            <span className="calcul-formula muted">—</span>
                          )}
                        </div>
                        <div className="col-impot-montant">
                          <div className="editable-amount">
                            <input
                              type="number"
                              value={exerciceOverrides[activeModal.exercice?.key]?.cvae ?? cvaeAmount}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setExerciceOverrides(prev => ({
                                  ...prev,
                                  [activeModal.exercice?.key]: {
                                    ...prev[activeModal.exercice?.key],
                                    cvae: isNaN(val) ? null : val
                                  }
                                }));
                              }}
                              className="amount-input"
                              disabled={!isApplicable}
                            />
                            <span className="currency">€</span>
                            {exerciceOverrides[activeModal.exercice?.key]?.cvae != null && (
                              <button
                                type="button"
                                className="btn-reset"
                                onClick={() => setExerciceOverrides(prev => ({
                                  ...prev,
                                  [activeModal.exercice?.key]: {
                                    ...prev[activeModal.exercice?.key],
                                    cvae: null
                                  }
                                }))}
                                title="Réinitialiser au calcul auto"
                              >↺</button>
                            )}
                          </div>
                          {exerciceOverrides[activeModal.exercice?.key]?.cvae == null && isApplicable && (
                            <span className="calc-indicator">auto</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Aide sur les règles */}
              <div className="impot-rules-help">
                <div className="rule-item">
                  <span className="rule-icon">🏢</span>
                  <span><strong>IS:</strong> 15% jusqu'à 42 500€ de bénéfice, 25% au-delà</span>
                </div>
                <div className="rule-item">
                  <span className="rule-icon">📋</span>
                  <span><strong>CFE:</strong> Base × Taux communal. Exo 100% an 1, -50% an 2</span>
                </div>
                <div className="rule-item">
                  <span className="rule-icon">📊</span>
                  <span><strong>CVAE:</strong> Due si CA ≥ 500k€. Taux progressif 0.25% à 0.75%</span>
                </div>
              </div>

              {/* Total */}
              {(() => {
                const totals = calculateExerciceTotals(activeModal.exercice);
                return (
                  <div className="modal-total">
                    <span>Total taxes ({activeModal.exercice?.label})</span>
                    <strong>-{formatCurrency(totals.totalImpotsEtTaxes)}</strong>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <span className="footer-hint">Calculs automatiques selon les règles fiscales</span>
              <div className="footer-buttons">
                <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal 64 - Charges de personnel - PER YEAR */}
      {activeModal?.type === 'chargePersonnel' && (() => {
        const yearKey = activeModal.exerciceKey;
        const yearManagers = getEffectiveManagers(yearKey);
        const yearEmployees = getEffectiveEmployees(yearKey);
        const yearHasEmployees = yearEmployees.hasEmployees;
        const yearProfiles = yearEmployees.profiles;

        // Determine if can add manager for this year
        const form = entityData.legalForm;
        const yearCanAddManager = form === 'SARL' || form === 'EURL' || form === 'SAS' || form === 'SASU';
        const yearAddManagerLabel = (form === 'SARL' || form === 'EURL') ? 'Ajouter un co-gérant' : (form === 'SAS' || form === 'SASU') ? 'Ajouter un DG' : null;

        // Parse exercice dates (format: DD/MM/YY)
        const parseExerciceDate = (dateStr) => {
          if (!dateStr) return null;
          const parts = dateStr.split('/');
          if (parts.length !== 3) return null;
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]) + (parseInt(parts[2]) < 50 ? 2000 : 1900);
          return new Date(year, month, day);
        };

        // Calculate proration factor for this exercice based on a start date (entity creation or manager entry)
        const calculateProrationForDate = (startDateInput) => {
          const exercice = activeModal.exercice;
          if (!exercice) return 1;

          const exerciceStart = parseExerciceDate(exercice.startDate);
          const exerciceEnd = parseExerciceDate(exercice.endDate);
          const startDate = startDateInput ? new Date(startDateInput) : null;

          if (!exerciceStart || !exerciceEnd || !startDate) return 1;

          // If start date is after exercice end, no activity this year
          if (startDate > exerciceEnd) return 0;

          // If start date is before or at exercice start, full year
          if (startDate <= exerciceStart) return 1;

          // Prorate: days from start to exercice end / total days in exercice
          const totalDays = Math.round((exerciceEnd - exerciceStart) / (24 * 60 * 60 * 1000)) + 1;
          const activeDays = Math.round((exerciceEnd - startDate) / (24 * 60 * 60 * 1000)) + 1;
          return activeDays / totalDays;
        };

        // Calculate proration for entity (uses entity creation date) - for employees
        const entityProrataFactor = calculateProrationForDate(entityData.creationDate);
        const entityProrataPercent = Math.round(entityProrataFactor * 100);

        // Calculate proration for a specific manager (uses salaryStartDate if available, then entryDate, then entity creation)
        const getManagerProration = (manager) => {
          const managerSalaryStartDate = manager.salaryStartDate || manager.entryDate || entityData.creationDate;
          return calculateProrationForDate(managerSalaryStartDate);
        };

        // Calculate exercice duration factor (how many 12-month periods this exercice represents)
        // IMPORTANT: Uses EFFECTIVE dates (clipped by forecast period) not fiscal year dates
        const getExerciceDurationFactor = () => {
          const exercice = activeModal.exercice;
          if (!exercice) return 1;
          let exerciceStart = parseExerciceDate(exercice.startDate);
          let exerciceEnd = parseExerciceDate(exercice.endDate);
          if (!exerciceStart || !exerciceEnd) return 1;

          // Clip by forecast dates if applicable
          const fcStart = forecastDates.startDate ? new Date(forecastDates.startDate) : null;
          const fcEnd = forecastDates.endDate ? new Date(forecastDates.endDate) : null;

          // First exercice: clip start date
          if (exercice.isFirstYear && fcStart && fcStart > exerciceStart) {
            exerciceStart = fcStart;
          }
          // Last exercice: clip end date (check if this is last in forecastExercices)
          const isLastExercice = forecastExercices.length > 0 &&
            forecastExercices[forecastExercices.length - 1].key === exercice.key;
          if (isLastExercice && fcEnd && fcEnd < exerciceEnd) {
            exerciceEnd = fcEnd;
          }

          const totalDays = Math.round((exerciceEnd - exerciceStart) / (24 * 60 * 60 * 1000)) + 1;
          const durationMonths = totalDays / 30.4375; // Average days per month (365.25/12)
          // Round to avoid floating point issues: 11.9-12.1 months = factor 1.0
          return (durationMonths >= 11.9 && durationMonths <= 12.1) ? 1 : durationMonths / 12;
        };
        const exerciceDurationFactor = getExerciceDurationFactor();

        // Check if a manager is the first mandatory manager (salary start date locked to creation date)
        const isFirstMandatoryManager = (manager) => {
          const isPresident = manager.role === 'president';
          const isGerant = manager.role === 'gerant';

          if ((form === 'SAS' || form === 'SASU') && isPresident) {
            const presidents = yearManagers.filter(m => m.role === 'president');
            return presidents.length > 0 && presidents[0].id === manager.id;
          }
          if ((form === 'SARL' || form === 'EURL') && isGerant) {
            const gerants = yearManagers.filter(m => m.role === 'gerant');
            return gerants.length > 0 && gerants[0].id === manager.id;
          }
          return false;
        };

        return (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal extra-wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4><span className="pcg-badge">64</span> Charges de personnel <span className="exercice-badge">{activeModal.exercice?.label}</span></h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-hint">
                  Saisissez les salaires BRUT - les charges patronales sont calculées automatiquement
                  {entityProrataPercent < 100 && (
                    <span className="prorata-hint"> — Prorata entité {entityProrataPercent}% (activité partielle sur cet exercice)</span>
                  )}
                </p>

                {/* Section Dirigeants */}
                <div className="personnel-section">
                  <div className="section-header">
                    <h5>Dirigeants</h5>
                  </div>

                  {yearManagers.length > 0 ? (
                    <div className="charges-table personnel-table">
                      <div className="table-header">
                        <div className="col-personnel-role">Fonction</div>
                        <div className="col-personnel-person">Personne <Tooltip text="Associez ce dirigeant à une personne physique du scénario. Cette liaison permet de reporter automatiquement ses revenus dans sa déclaration fiscale personnelle."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-status">Statut <Tooltip text="TNS = Travailleur Non Salarié (cotisations SSI ~45% du brut). AS = Assimilé Salarié (cotisations régime général ~54% du brut). Le NET perçu = Brut × 0.78 pour AS, Brut × 0.55 pour TNS."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire BRUT mensuel du dirigeant. Le NET perçu sera calculé automatiquement (après déduction des charges salariales ~22% pour AS)."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-months">Mois <Tooltip text="Option de versement du salaire : 12 = standard, 13 = avec 13ème mois (prime fin d'année), 14 = avec 13ème + 14ème mois. Cette prime est versée en plus des 12 mois de base."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-salary-start">Début rém. <Tooltip text="Date à partir de laquelle le dirigeant commence à percevoir sa rémunération. Si cette date est dans l'exercice, le salaire sera proratisé proportionnellement au nombre de jours travaillés."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-annual">Brut annuel <Tooltip text="Salaire brut total pour cet exercice = (Brut/mois × 12 + primes 13e/14e mois) × prorata de présence. Le pourcentage entre parenthèses indique le taux de prorata si inférieur à 100%."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-charges">Charges pat. <Tooltip text="Charges patronales payées par l'entreprise en plus du salaire brut. TNS ~45% du brut, AS ~54% du brut. Si ACRE activé, réduction de 50% la première année."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-cout">Coût total <Tooltip text="Coût total pour l'entreprise = Brut annuel + Charges patronales. C'est ce montant qui impacte le résultat de l'exercice."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-actions"></div>
                      </div>
                      <div className="table-body">
                        {yearManagers.map((manager) => {
                          // Base annual NET salary = net mensuel × mois multiplier
                          const annualBase = manager.netMonthly * manager.months;
                          // Apply exercice duration factor (for exercices longer/shorter than 12 months)
                          // AND per-manager proration (fraction of exercice worked)
                          const managerProrata = getManagerProration(manager);
                          const managerProrataPercent = Math.round(managerProrata * 100);
                          // Exercice NET salary = annual NET × (exercice months / 12) × manager proration
                          const annualNet = Math.round(annualBase * exerciceDurationFactor * managerProrata);
                          const calcResult = calculateChargesFromNet(annualNet, manager.status);
                          let chargesPatronales = calcResult.chargesPatronales;

                          // ACRE année 1: -50% charges
                          if (hasACRE && activeModal.exercice?.isFirstYear) {
                            chargesPatronales = Math.round(chargesPatronales * 0.5);
                          }

                          const coutTotal = calcResult.brut + chargesPatronales;

                          return (
                            <div key={manager.id} className="table-row personnel-row">
                              <div className="col-personnel-role">
                                <span className="role-name">{getRoleLabel(manager.role)}</span>
                              </div>
                              <div className="col-personnel-person">
                                <select
                                  value={manager.individualId || ''}
                                  onChange={(e) => handleManagerChangeForYear(yearKey, manager.id, 'individualId', e.target.value ? parseInt(e.target.value) : null)}
                                  className="person-select"
                                >
                                  <option value="">— Non lié —</option>
                                  {scenarioIndividuals.map(ind => (
                                    <option key={ind.id} value={ind.id}>
                                      {`${ind.first_name || ''} ${ind.last_name || ''}`.trim() || 'Individu'}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-personnel-status">
                                <span
                                  className={`status-tag ${manager.status === 'TNS' ? 'tns' : 'as'}`}
                                  title={manager.status === 'TNS' ? 'Travailleur Non Salarié - Cotisations SSI ~45%' : 'Assimilé Salarié - Régime général ~54%'}
                                >
                                  {calcResult.statusDisplay || (manager.status === 'ASSIMILE_SALARIE' ? 'AS' : manager.status)}
                                </span>
                              </div>
                              <div className="col-personnel-brut">
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    value={manager.netMonthly || ''}
                                    onChange={(e) => handleManagerChangeForYear(yearKey, manager.id, 'netMonthly', parseFloat(e.target.value) || 0)}
                                    min="0"
                                    step="100"
                                  />
                                  <span>€</span>
                                </div>
                              </div>
                              <div className="col-personnel-months">
                                <input
                                  type="number"
                                  value={manager.months || ''}
                                  onChange={(e) => handleManagerChangeForYear(yearKey, manager.id, 'months', parseInt(e.target.value) || 0)}
                                  min="0"
                                  max="14"
                                  className="months-input"
                                />
                              </div>
                              <div className="col-personnel-salary-start">
                                <input
                                  type="date"
                                  value={manager.salaryStartDate || entityData.creationDate || ''}
                                  onChange={(e) => handleManagerChangeForYear(yearKey, manager.id, 'salaryStartDate', e.target.value)}
                                  min={entityData.creationDate || ''}
                                  className={`date-input ${isFirstMandatoryManager(manager) ? 'locked' : ''}`}
                                  disabled={isFirstMandatoryManager(manager)}
                                  title={isFirstMandatoryManager(manager) ? 'Date de début = date de création (premier dirigeant)' : ''}
                                />
                              </div>
                              <div className="col-personnel-annual">
                                {calcResult.brut === 0 ? (
                                  <span className="non-remunere">Non rémunéré</span>
                                ) : (
                                  <span className="annual-value">
                                    {formatCurrency(calcResult.brut)}
                                    {managerProrataPercent < 100 && (
                                      <span className="prorata-badge" title={`Prorata ${managerProrataPercent}% basé sur date début rémunération: ${manager.salaryStartDate || entityData.creationDate || 'non définie'}`}>
                                        ({managerProrataPercent}%)
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                              <div className="col-personnel-charges">
                                {calcResult.brut === 0 ? (
                                  <span className="non-remunere">—</span>
                                ) : (
                                  <>
                                    <span className="charges-value">
                                      +{formatCurrency(chargesPatronales)}
                                      {hasACRE && activeModal.exercice?.isFirstYear && (
                                        <span className="tag-reduction">ACRE</span>
                                      )}
                                    </span>
                                    <span className="charges-rate">({Math.round(calcResult.tauxCharges * 100)}%)</span>
                                  </>
                                )}
                              </div>
                              <div className="col-personnel-cout">
                                {calcResult.brut === 0 ? (
                                  <span className="non-remunere">—</span>
                                ) : (
                                  <strong>{formatCurrency(coutTotal)}</strong>
                                )}
                              </div>
                              <div className="col-personnel-actions">
                                {manager.individualId && (
                                  <button
                                    type="button"
                                    className="btn-show-relation"
                                    onClick={() => {
                                      // Dispatch event to highlight relation on canvas
                                      window.dispatchEvent(new CustomEvent('highlightRelation', {
                                        detail: { individualId: manager.individualId, entityId }
                                      }));
                                      onClose();
                                    }}
                                    title="Voir la relation sur le canvas"
                                  >
                                    ↗
                                  </button>
                                )}
                                {yearManagers.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn-remove"
                                    onClick={() => removeManagerForYear(yearKey, manager.id)}
                                    title="Supprimer ce dirigeant"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="no-data">Aucun dirigeant configuré</p>
                  )}

                  {/* Bouton ajouter dirigeant */}
                  {yearCanAddManager && yearAddManagerLabel && (
                    <button type="button" className="btn-add-manager modal-add" onClick={() => addManagerForYear(yearKey)}>
                      + {yearAddManagerLabel}
                    </button>
                  )}
                </div>

                {/* Section Employés */}
                <div className="personnel-section">
                  <div className="section-header">
                    <h5>Employés <Tooltip text="Personnel salarié hors dirigeants. Charges patronales ~45% du brut."><span className="help-icon">ⓘ</span></Tooltip></h5>
                    <div className="header-controls">
                      <label className="toggle-checkbox">
                        <input
                          type="checkbox"
                          checked={yearHasEmployees}
                          onChange={(e) => setHasEmployeesForYear(yearKey, e.target.checked)}
                        />
                        <span>Activer</span>
                      </label>
                      {yearHasEmployees && (
                        <div className="mode-toggle compact">
                          <button className={getStep3Mode() === 'simplified' ? 'active' : ''} onClick={() => setStep3Mode('simplified')}>Agrégé</button>
                          <button className={getStep3Mode() === 'detailed' ? 'active' : ''} onClick={() => setStep3Mode('detailed')}>Détaillé</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {yearHasEmployees && getStep3Mode() === 'simplified' && (
                    /* Mode agrégé */
                    <div className="charges-table personnel-table">
                      <div className="table-header">
                        <div className="col-personnel-role">Type</div>
                        <div className="col-personnel-count">Effectif <Tooltip text="Nombre total d'employés en équivalent temps plein (ETP). Ex: 2 mi-temps = 1 ETP."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire brut mensuel moyen par employé. Le brut inclut le salaire de base + primes + avantages."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-months">Mois <Tooltip text="Nombre de mois de travail effectif par an. 12 = année complète. Moins de 12 pour les CDD ou temps partiels annualisés."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-charges">Charges pat. <Tooltip text="Charges patronales ~45% du brut. Comprend : cotisations sociales, retraite, prévoyance, mutuelle, formation, etc."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-cout">Coût total <Tooltip text="Coût total pour l'entreprise = Brut × Effectif × Mois × 1.45 (charges patronales). C'est ce montant qui impacte le résultat de l'exercice."><span className="help-icon">ⓘ</span></Tooltip></div>
                      </div>
                      <div className="table-body">
                        <div className="table-row personnel-row">
                          <div className="col-personnel-role">
                            <span className="role-name">Tous employés</span>
                          </div>
                          <div className="col-personnel-count">
                            <input
                              type="number"
                              value={yearEmployees.count || ''}
                              onChange={(e) => updateEmployeesAggregatedForYear(yearKey, { count: parseInt(e.target.value) || 0 })}
                              min="0"
                              className="count-input"
                            />
                          </div>
                          <div className="col-personnel-brut">
                            <div className="input-with-suffix">
                              <input
                                type="number"
                                value={yearEmployees.avgGrossSalary || ''}
                                onChange={(e) => updateEmployeesAggregatedForYear(yearKey, { avgGrossSalary: parseFloat(e.target.value) || 0 })}
                                min="0"
                                step="100"
                              />
                              <span>€</span>
                            </div>
                          </div>
                          <div className="col-personnel-months">
                            <input
                              type="number"
                              value={yearEmployees.months || ''}
                              onChange={(e) => updateEmployeesAggregatedForYear(yearKey, { months: parseInt(e.target.value) || 0 })}
                              min="0"
                              max="12"
                              className="months-input"
                            />
                          </div>
                          <div className="col-personnel-charges">
                            {(() => {
                              const masseBruteBase = yearEmployees.count * yearEmployees.avgGrossSalary * yearEmployees.months;
                              const masseBrute = Math.round(masseBruteBase * entityProrataFactor);
                              const charges = Math.round(masseBrute * 0.45);
                              return (
                                <>
                                  <span className="charges-value">+{formatCurrency(charges)}</span>
                                  <span className="charges-rate">(45%)</span>
                                </>
                              );
                            })()}
                          </div>
                          <div className="col-personnel-cout">
                            {(() => {
                              const masseBruteBase = yearEmployees.count * yearEmployees.avgGrossSalary * yearEmployees.months;
                              const masseBrute = Math.round(masseBruteBase * entityProrataFactor);
                              return <strong>{formatCurrency(Math.round(masseBrute * 1.45))}</strong>;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {yearHasEmployees && getStep3Mode() === 'detailed' && (
                    /* Mode détaillé */
                    <div className="charges-table personnel-table detailed">
                      <div className="table-header">
                        <div className="col-personnel-name">Poste / Nom</div>
                        <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire brut mensuel pour ce poste. Le brut inclut le salaire de base + primes + avantages."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-months">Mois <Tooltip text="Nombre de mois de travail effectif par an. 12 = année complète. Peut être inférieur pour les CDD ou temps partiels annualisés."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-count">Effectif <Tooltip text="Nombre de personnes occupant ce poste (en ETP). Permet de multiplier le coût unitaire."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-charges">Charges pat. <Tooltip text="Charges patronales ~45% du brut. Comprend : cotisations sociales, retraite, prévoyance, mutuelle, formation."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-cout">Coût annuel <Tooltip text="Coût total annuel pour l'entreprise = Brut × Mois × Effectif × 1.45. Ce montant impacte directement le résultat de l'exercice."><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-actions"></div>
                      </div>
                      <div className="table-body">
                        {yearProfiles.length > 0 ? (
                          yearProfiles.map(profile => {
                            const months = profile.months || 12;
                            // Apply proration for this exercice (using entity creation date)
                            const annualBase = profile.grossMonthlySalary * months * profile.headcount;
                            const annualBrut = Math.round(annualBase * entityProrataFactor);
                            const charges = Math.round(annualBrut * 0.45);
                            const coutTotal = annualBrut + charges;
                            return (
                              <div key={profile.id} className="table-row personnel-row">
                                <div className="col-personnel-name">
                                  <input
                                    type="text"
                                    value={profile.profileName || ''}
                                    onChange={(e) => updateEmployeeProfileForYear(yearKey, profile.id, 'profileName', e.target.value)}
                                    placeholder="Ex: Commercial, Assistant..."
                                    className="input-name"
                                  />
                                </div>
                                <div className="col-personnel-brut">
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={profile.grossMonthlySalary || ''}
                                      onChange={(e) => updateEmployeeProfileForYear(yearKey, profile.id, 'grossMonthlySalary', parseInt(e.target.value) || 0)}
                                      min="0"
                                      step="100"
                                    />
                                    <span>€</span>
                                  </div>
                                </div>
                                <div className="col-personnel-months">
                                  <input
                                    type="number"
                                    value={months}
                                    onChange={(e) => updateEmployeeProfileForYear(yearKey, profile.id, 'months', parseInt(e.target.value) || 12)}
                                    min="0"
                                    max="12"
                                    className="months-input"
                                  />
                                </div>
                                <div className="col-personnel-count">
                                  <input
                                    type="number"
                                    value={profile.headcount || ''}
                                    onChange={(e) => updateEmployeeProfileForYear(yearKey, profile.id, 'headcount', parseInt(e.target.value) || 1)}
                                    min="1"
                                    className="count-input"
                                  />
                                </div>
                                <div className="col-personnel-charges">
                                  <span className="charges-value">+{formatCurrency(charges)}</span>
                                  <span className="charges-rate">(45%)</span>
                                </div>
                                <div className="col-personnel-cout">
                                  <strong>{formatCurrency(coutTotal)}</strong>
                                </div>
                                <div className="col-personnel-actions">
                                  <button
                                    type="button"
                                    className="btn-remove"
                                    onClick={() => removeEmployeeProfileForYear(yearKey, profile.id)}
                                    title="Supprimer"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="table-row empty-row">
                            <div className="empty-message">Aucun employé ajouté</div>
                          </div>
                        )}
                        <div className="table-row add-row">
                          <div className="add-cell">
                            <button type="button" className="btn-add-employee" onClick={() => addEmployeeProfileForYear(yearKey)}>
                              + Ajouter un employé
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!yearHasEmployees && (
                    <p className="no-data muted">Pas d'employés (hors dirigeants)</p>
                  )}
                </div>

                {/* Légende */}
                <div className="modal-legend">
                  <div className="legend-item">
                    <span className="legend-input"></span>
                    <span>Champ éditable</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-calc"></span>
                    <span>Valeur calculée automatiquement</span>
                  </div>
                </div>

                <div className="impot-rules-help">
                  <div className="rule-item">
                    <span className="rule-icon">1</span>
                    <span><strong>Coût total</strong> = Salaire BRUT + Charges patronales</span>
                  </div>
                  <div className="rule-item">
                    <span className="rule-icon">2</span>
                    <span><strong>TNS</strong> ~45% | <strong>AS</strong> ~54% | <strong>Employés</strong> ~45%</span>
                  </div>
                </div>

                {/* Total */}
                <div className="modal-total">
                  <span>Total compte 64 ({activeModal.exercice?.label})</span>
                  <strong>-{formatCurrency(calculateExerciceTotals(activeModal.exercice).coutDirigeants + calculateExerciceTotals(activeModal.exercice).coutEmployes)}</strong>
                </div>
              </div>
              <div className="modal-footer">
                <span className="footer-hint">BRUT + Charges patronales = Coût entreprise ({activeModal.exercice?.label})</span>
                <div className="footer-buttons">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                  <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal détail affectation du résultat */}
      {activeModal?.type === 'affectationDetail' && (() => {
        const { exercice, affectation } = activeModal;

        return (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal affectation-detail-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4>📋 Affectation du résultat - {exercice.label}</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                {/* Calcul du distribuable */}
                <div className="affectation-section">
                  <h5>Calcul du distribuable</h5>
                  <div className="affectation-detail-grid">
                    <div className="detail-row">
                      <span className="detail-label">Résultat Net</span>
                      <span className="detail-value">{formatCurrency(affectation.resultatNet)}</span>
                    </div>
                    <div className="detail-row indent">
                      <span className="detail-label">− Réserve légale (5%)</span>
                      <span className="detail-value neg">-{formatCurrency(affectation.reserveLegale)}</span>
                    </div>
                    <div className="detail-row sub-hint">
                      <span className="detail-label-small">
                        Plafond: 10% du capital = {formatCurrency(affectation.reserveLegalePlafond)}
                        {affectation.cumulReserves > 0 && ` • Cumulé avant: ${formatCurrency(affectation.cumulReserves)}`}
                        {affectation.reserveRestante === 0 && ' • Plafond atteint'}
                      </span>
                    </div>
                    {affectation.reportNMoins1 !== 0 && (
                      <div className="detail-row indent">
                        <span className="detail-label">{affectation.reportNMoins1 > 0 ? '+' : ''} Report à nouveau N-1</span>
                        <span className={`detail-value ${affectation.reportNMoins1 > 0 ? 'pos' : 'neg'}`}>
                          {affectation.reportNMoins1 > 0 ? '+' : ''}{formatCurrency(affectation.reportNMoins1)}
                        </span>
                      </div>
                    )}
                    <div className="detail-row total">
                      <span className="detail-label">= DISTRIBUABLE</span>
                      <span className="detail-value">{formatCurrency(affectation.distribuable)}</span>
                    </div>
                  </div>
                </div>

                {/* Décision AG */}
                <div className="affectation-section">
                  <h5>Décision AG du {affectation.agDateFormatted}</h5>
                  <p className="section-hint">Distribution à {affectation.percent}% du distribuable</p>
                  <div className="affectation-detail-grid">
                    <div className="detail-row highlight-green">
                      <span className="detail-label">💰 Dividendes ({affectation.percent}%)</span>
                      <span className="detail-value">{formatCurrency(affectation.dividendes)}</span>
                    </div>
                    <div className="detail-row sub-hint">
                      <span className="detail-label-small">Versement prévu: {affectation.paymentDateFormatted} → IR {affectation.taxYear}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">➡️ Report à nouveau</span>
                      <span className="detail-value">{formatCurrency(affectation.reportNouveau)}</span>
                    </div>
                    <div className="detail-row sub-hint">
                      <span className="detail-label-small">→ Reporté sur l'exercice suivant</span>
                    </div>
                  </div>
                </div>

                {/* Info prélèvements */}
                <div className="affectation-note">
                  <span className="note-icon">ℹ️</span>
                  <span>Les dividendes seront soumis au PFU (30%) ou au barème progressif selon le choix de chaque associé.</span>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal détail dividendes */}
      {activeModal?.type === 'dividendDetail' && (() => {
        const { exercice, divInfo, resultatNet } = activeModal;
        const acompteIR = Math.round(divInfo.amount * 0.128);
        const acomptePS = Math.round(divInfo.amount * 0.172);
        const totalAcompte = acompteIR + acomptePS;
        const netVerse = divInfo.amount - totalAcompte;

        return (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal dividend-detail-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4>💰 Distribution dividendes - {exercice.label}</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="dividend-detail-grid">
                  <div className="detail-row">
                    <span className="detail-label">Résultat net exercice</span>
                    <span className="detail-value">{formatCurrency(resultatNet)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Taux distribution</span>
                    <span className="detail-value">{divInfo.percent}%</span>
                  </div>
                  <div className="detail-row highlight">
                    <span className="detail-label">Dividendes bruts</span>
                    <span className="detail-value">{formatCurrency(divInfo.amount)}</span>
                  </div>
                </div>

                <div className="dividend-detail-section">
                  <h5>📅 Versement prévu: {divInfo.paymentDateFormatted}</h5>
                  <p className="tax-year-info">→ Imposé sur l'IR {divInfo.taxYear} des associés</p>
                </div>

                <div className="dividend-detail-section">
                  <h5>📤 Prélèvements à la source</h5>
                  <p className="section-hint">La société prélève et verse à l'État:</p>
                  <div className="dividend-detail-grid">
                    <div className="detail-row">
                      <span className="detail-label">Acompte IR (12,8%)</span>
                      <span className="detail-value neg">-{formatCurrency(acompteIR)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Prélèvements sociaux (17,2%)</span>
                      <span className="detail-value neg">-{formatCurrency(acomptePS)}</span>
                    </div>
                    <div className="detail-row total">
                      <span className="detail-label">Total prélevé (30%)</span>
                      <span className="detail-value neg">-{formatCurrency(totalAcompte)}</span>
                    </div>
                  </div>
                </div>

                <div className="dividend-detail-section result">
                  <div className="detail-row highlight-green">
                    <span className="detail-label">👥 Net versé aux associés</span>
                    <span className="detail-value">{formatCurrency(netVerse)}</span>
                  </div>
                </div>

                <div className="dividend-detail-note">
                  <span className="note-icon">⚠️</span>
                  <span>Impact trésorerie société: -{formatCurrency(divInfo.amount)} (dividendes) incluant le versement de l'acompte au Trésor Public</span>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
              </div>
            </div>
          </div>
        );
      })()}
        </div>,
        document.body
      )}
    </div>
  );
};

export default EntityEditPage;
